import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { ContextCheck, BalancedView, CorrectAdvisor, SafetySupport, ContextSuggestions, getSavedCountry } from "@/components/BalancedGuidance";
import { ProfileContextMemory } from "@/components/ProfileContextMemory";
import { pronounSet } from "@/lib/pronouns";
import { useAuth } from "@/context/AuthContext";
import { useDiscreet } from "@/context/DiscreetContext";
import { Sparkles, Plus, Trash2, ShieldCheck, Download, AlertCircle, BookOpen, Send, Heart, Eye, EyeOff, ArrowLeft, ArrowRight, CreditCard, RefreshCw, X, Check, Mail, Settings as Cog, Calendar, Search, Mic } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { AdminAIOrch } from "@/components/AdminAIOrch";
import { AdminOntology } from "@/components/AdminOntology";
import { AdminOntologyGovernance } from "@/components/AdminOntologyGovernance";
import { AdminExport } from "@/components/AdminExport";
import { AdminContact } from "@/components/AdminContact";
import { AdminIntelligenceConsole } from "@/components/AdminIntelligenceConsole";
import { AdminExpertReview } from "@/components/AdminExpertReview";
import { AdminAmbassadorMessages } from "@/components/AdminAmbassadorMessages";
import { AdminContinuityPanel } from "@/components/AdminContinuityPanel";
import { AdvisorFeedback } from "@/components/AdvisorFeedback";
import { RecommendationOutcomes } from "@/components/RecommendationOutcomes";
import UsageMeter from "@/components/UsageMeter";
import { PersonaTimeline } from "@/components/PersonaTimeline";
import { VoiceInput, VoiceTeaser } from "@/components/VoiceInput";
import { SquareCardStep } from "@/components/SquareCardStep";
import UpgradeModal from "@/components/UpgradeModal";
import AdminGovernance from "@/components/AdminGovernance";

const PLAN_LABEL = { free: "Free", lite: "Lite", plus: "Plus", premium: "Premium", luxe: "Luxe", sense_plus: "Sense+" };
const SKU_LABEL = {
  sense_plus_monthly: "Sense+ Monthly",
  sense_plus_annual:  "Sense+ Annual",
  luxe_monthly:       "Luxe Monthly",
  luxe_annual:        "Luxe Annual",
};
const SKU_PRICE = { sense_plus_monthly: "$24/mo", sense_plus_annual: "$230/yr", luxe_monthly: "$59/mo", luxe_annual: "$566/yr" };
const STAGE_LABEL = { talking: "Talking", early: "Early", established: "Established", long_term: "Long-term", ending: "Ending" };
const TONE_LABEL = { soft: "Soft", direct: "Direct", playful: "Playful", elegant: "Elegant", warm_firm: "Warm + Firm", boundary: "Boundary", appreciative: "Appreciative", clarifying: "Clarifying" };

function Pill({ children, color = "champagne" }) {
  const cls = color === "mauve" ? "bg-mauve/10 text-mauve" : color === "sage" ? "bg-sage/30 text-cocoa" : color === "rose" ? "bg-rose/20 text-mauve" : "bg-champagne text-cocoa";
  return <span className={`inline-block text-[11px] tracking-wide uppercase px-2.5 py-1 rounded-full ${cls}`}>{children}</span>;
}

function Maybe({ children, className = "" }) {
  const { discreet } = useDiscreet();
  return <span className={`${discreet ? "discreet-blur" : ""} ${className}`}>{children}</span>;
}

function RecentHistoryStrip() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/history?limit=3").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  if (!items || items.length === 0) return null;
  return (
    <section data-testid="dashboard-recent-history" className="mt-7">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">Pick up where you left off</p>
        <Link to="/history" className="text-xs text-mauve underline" data-testid="dashboard-history-link">All history</Link>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {items.map((it) => (
          <Link key={it.id} to={`/history/${it.kind}/${it.id}`} className="ss-card p-4 hover:border-mauve/50 transition-colors" data-testid={`dashboard-history-${it.id}`}>
            <p className="text-[10px] uppercase tracking-widest text-cocoa/45">{it.kind === "advisor" ? "Advisor read" : it.kind === "mentor" ? "Mentor chat" : it.kind}{it.profile_alias ? ` · ${it.profile_alias}` : ""}</p>
            <p className="mt-1 text-sm text-cocoa leading-snug line-clamp-2"><Maybe>{it.title}</Maybe></p>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─── DASHBOARD ─────────────────────────────────────────────────────────────
export function Dashboard() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [usage, setUsage] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [emerging, setEmerging] = useState([]);
  const [checkIn, setCheckIn] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [continuity, setContinuity] = useState([]);
  const [calibStatus, setCalibStatus] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  useEffect(() => {
    api.get("/history?limit=1").then((r) => setHistoryCount(r.data?.total || 0)).catch(() => {});
    api.get("/profiles").then((r) => setProfiles(r.data || []));
    api.get("/advisor/usage").then((r) => setUsage(r.data));
    api.get("/drafts").then((r) => setDrafts((r.data || []).slice(0, 3)));
    api.get("/dashboard/emerging").then((r) => setEmerging(r.data || [])).catch(() => {});
    api.get("/dashboard/daily-check-in").then((r) => setCheckIn(r.data)).catch(() => {});
    api.get("/dashboard/dynamic-shifts").then((r) => setShifts(r.data?.shifts || [])).catch(() => {});
    api.get("/intelligence/continuity").then((r) => setContinuity(r.data?.observations || [])).catch(() => {});
    api.get("/calibration/status").then((r) => setCalibStatus(r.data)).catch(() => {});
  }, []);
  const aliasOf = (id) => profiles.find((p) => p.id === id)?.alias || "—";
  const started = profiles.length > 0 || drafts.length > 0 || historyCount > 0 || shifts.length > 0 || continuity.length > 0;

  return (
    <div data-testid="dashboard-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Welcome back, {user?.display_name || user?.name || "friend"}</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-cocoa mt-1">Your sense room.</h1>
        </div>
        <Link to="/ask"><button data-testid="dashboard-ask-btn" className="ss-pill ss-pill-gold h-11"><Sparkles size={14} /> Ask SugarSense</button></Link>
      </div>

      {/* Calibration nudge — shown only if user hasn't calibrated yet */}
      {calibStatus && !calibStatus.has_calibrated && (
        <Link to="/calibration" data-testid="dashboard-calibration-nudge"
          className="mt-6 block ss-card-premium p-5 hover:border-mauve transition-colors">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Calibrate your read</p>
              <p className="font-heading text-xl text-cocoa mt-1 leading-tight">A 4-minute reflective conversation, before we begin.</p>
              <p className="text-[13px] text-cocoa/65 italic mt-1">It helps SugarSense read your patterns more accurately. You can refine it anytime.</p>
            </div>
            <span className="ss-pill ss-pill-gold h-10 shrink-0"><Sparkles size={12} /> Begin</span>
          </div>
        </Link>
      )}

      {/* — Plan + advisor capacity meter — */}
      <div className="mt-6">
        <UsageMeter compact />
      </div>

      <VoiceTeaser />

      {/* Activity sections appear only once the member has started; a fresh account sees one plain sentence. */}
      {!started ? (
        <section data-testid="dashboard-activity-empty" className="mt-7 ss-card p-6 text-center">
          <p className="text-sm text-cocoa/65">Your activity will display here once you get started with SugarSense.</p>
        </section>
      ) : (<>
      {/* — Today's dynamic — */}
      {checkIn?.body && (
        <section data-testid="dashboard-daily-checkin" className="mt-7 ss-card-premium p-6 sm:p-7 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(166,106,114,0.18), transparent 65%)" }} />
          <p className="text-[10px] tracking-[0.4em] uppercase text-mauve relative">Today</p>
          <p className="mt-3 font-heading italic text-2xl sm:text-3xl text-cocoa leading-snug relative">"{checkIn.body}"</p>
          {checkIn.scope === "persona" && checkIn.alias && (
            <p data-testid="dashboard-checkin-persona" className="mt-2 text-xs text-cocoa/55 relative">About <strong className="text-cocoa">{checkIn.alias}</strong></p>
          )}
          {checkIn.scope === "multi" && Array.isArray(checkIn.items) && checkIn.items.length > 0 && (
            <ul data-testid="dashboard-checkin-items" className="mt-5 space-y-3 relative">
              {checkIn.items.map((it) => (
                <li key={it.profile_id} data-testid={`dashboard-checkin-item-${it.profile_id}`} className="rounded-xl bg-ivory/70 border border-champagne/50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{it.alias}</p>
                    <Link to={`/ask?profile_id=${it.profile_id}`} data-testid={`dashboard-checkin-ask-${it.profile_id}`} className="text-xs text-mauve underline">Ask about {it.alias}</Link>
                  </div>
                  <p className="mt-1.5 font-heading italic text-lg text-cocoa leading-snug">"{it.body}"</p>
                  {it.suggested_essay?.slug && (
                    <Link to={`/essays/${it.suggested_essay.slug}?profile_id=${it.profile_id}`} className="mt-2 inline-block text-xs text-cocoa/60 underline">Read: {it.suggested_essay.title}</Link>
                  )}
                </li>
              ))}
            </ul>
          )}
          {checkIn.scope !== "multi" && checkIn.suggested_essay?.slug && (
            <div className="mt-5 pt-4 border-t border-champagne/40 relative" data-testid="dashboard-checkin-suggested-essay">
              <p className="text-[10px] tracking-[0.35em] uppercase text-mauve mb-2">A reading tailored for you</p>
              <Link
                to={`/essays/${checkIn.suggested_essay.slug}`}
                data-testid="dashboard-checkin-suggested-essay-link"
                className="group inline-flex items-start gap-2 text-cocoa hover:text-mauve transition-colors"
              >
                <BookOpen size={16} className="mt-1 shrink-0 text-mauve" />
                <span>
                  <span className="font-heading text-lg leading-tight block">{checkIn.suggested_essay.title}</span>
                  {checkIn.suggested_essay.subtitle && (
                    <span className="text-xs text-cocoa/60 italic block mt-0.5">{checkIn.suggested_essay.subtitle}</span>
                  )}
                  <span className="text-xs text-mauve underline mt-1 inline-block">Read this →</span>
                </span>
              </Link>
            </div>
          )}
        </section>
      )}

      <RecentHistoryStrip />

      {/* — Dynamic shifts — */}
      {shifts.length > 0 && (
        <section data-testid="dashboard-shifts" className="mt-7">
          <p className="text-[10px] tracking-[0.35em] uppercase text-mauve mb-3">Recent shifts</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {shifts.map((s, i) => (
              <div key={i} data-testid={`shift-${s.kind}`} className="ss-card p-5">
                <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{s.kind === "archetype_change" ? "Pattern shift" : s.kind === "phase_change" ? "Phase shift" : s.kind}</p>
                <p className="mt-2 text-cocoa leading-snug">{s.summary}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* — Recurring patterns (Stage D2 continuity intelligence) — */}
      {continuity.length > 0 && (
        <section data-testid="dashboard-continuity" className="mt-7">
          <div className="flex items-end justify-between mb-3">
            <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">Recurring patterns SugarSense is tracking</p>
            <Link to="/intelligence" data-testid="dashboard-intel-link" className="text-[11px] text-mauve hover:text-cocoa underline">See all + search →</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {continuity.slice(0, 4).map((o, i) => (
              <div key={i} data-testid={`continuity-${o.observation_key}`} className="ss-card p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-heading text-lg text-cocoa leading-tight">{o.human_label}</p>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-mauve shrink-0">
                    {o.occurrences}× observed
                  </span>
                </div>
                <p className="mt-2 text-cocoa/70 text-sm leading-relaxed">
                  Confidence {Math.round((o.confidence || 0) * 100)}% · last seen {(o.last_seen_at || "").slice(0, 10)}
                </p>
                <p className="mt-3 text-[11px] tracking-[0.25em] uppercase text-cocoa/40">
                  Platform-owned longitudinal intelligence
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* — What's emerging — */}
      {emerging.length > 0 && (
        <section data-testid="dashboard-emerging" className="mt-7">
          <p className="text-[10px] tracking-[0.35em] uppercase text-mauve mb-3">What's emerging</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {emerging.map((n, i) => (
              <Link to={n.action_path} key={i} data-testid={`emerging-${n.kind}`} className="ss-card-premium p-5 block group hover:shadow-md transition-shadow">
                <p className="font-heading text-lg text-cocoa group-hover:text-mauve transition-colors leading-snug"><Maybe>{n.headline}</Maybe></p>
                {n.subtext && <p className="mt-2 text-sm text-cocoa/65">{n.subtext}</p>}
                <p className="mt-4 text-xs tracking-widest uppercase text-mauve">{n.cta} →</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      </>)}

      {usage && (
        <div className="mt-6 ss-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="dashboard-usage">
          <div>
            <p className="text-xs uppercase tracking-widest text-cocoa/50">Your plan</p>
            <p className="font-heading text-2xl text-cocoa">{PLAN_LABEL[usage.plan]}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-cocoa/50">AI insights used</p>
            <p className="font-heading text-2xl text-cocoa">{usage.used} <span className="text-cocoa/40 text-base">/ {usage.limit}</span></p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-cocoa/50">Profiles</p>
            <p className="font-heading text-2xl text-cocoa">{profiles.length} <span className="text-cocoa/40 text-base">/ {usage.max_profiles === 9999 ? "∞" : usage.max_profiles}</span></p>
          </div>
          {usage.plan === "free" && <Link to="/billing"><button className="ss-pill ss-pill-primary h-10">Upgrade</button></Link>}
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-2 gap-5">
        <section className="ss-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl text-cocoa">The people you're reading</h2>
            <Link to="/profiles"><span className="text-sm text-mauve">All →</span></Link>
          </div>
          {profiles.length === 0 ? (
            <EmptyState
              icon={Heart}
              title="No reads yet."
              body="Start by adding the person you're navigating — alias only, no real names."
              ctaText="Add your first profile"
              ctaTo="/profiles"
            />
          ) : (
            <ul className="mt-4 space-y-3">
              {profiles.slice(0, 4).map((p) => (
                <li key={p.id} data-testid={`dashboard-profile-${p.id}`} className="flex items-center justify-between border-b border-champagne pb-3 last:border-0 gap-2">
                  <div className="min-w-0 flex-1">
                    <Maybe className="font-heading text-lg text-cocoa">{p.alias}</Maybe>
                    <p className="text-xs text-cocoa/50 mt-0.5">{STAGE_LABEL[p.stage] || "—"} · {p.city || "—"}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <Link to={`/ask?profile_id=${p.id}`} data-testid={`dashboard-ask-${p.id}`}><span className="ss-pill ss-pill-gold h-9"><Sparkles size={12} /> Ask</span></Link>
                    <Link to={`/profiles/${p.id}`}><span className="ss-pill ss-pill-ghost h-9">Open</span></Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="ss-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl text-cocoa">Recent drafts</h2>
            <Link to="/drafts"><span className="text-sm text-mauve">All →</span></Link>
          </div>
          {drafts.length === 0 ? (
            <EmptyState
              icon={Send}
              title="No drafts yet."
              body="When you ask SugarSense about a real moment, you'll get a message draft you can send."
              ctaText="Ask SugarSense"
              ctaTo="/ask"
            />
          ) : (
            <ul className="mt-4 space-y-3">
              {drafts.map((d) => (
                <li key={d.id} data-testid={`dashboard-draft-${d.id}`} className="border-b border-champagne pb-3 last:border-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill color="rose">{TONE_LABEL[d.tone]}</Pill>
                    {d.profile_id && <Maybe className="text-xs text-cocoa/55">for {aliasOf(d.profile_id)}</Maybe>}
                  </div>
                  <Maybe className="block mt-2 text-sm text-cocoa/80 line-clamp-3">{d.draft}</Maybe>
                  <button onClick={() => { navigator.clipboard.writeText(d.draft); }} className="mt-2 text-[11px] uppercase tracking-widest text-mauve hover:text-cocoa">Copy</button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body, ctaText, ctaTo }) {
  return (
    <div className="mt-4 p-5 sm:p-6 text-center bg-blush/15 border border-champagne/70 border-dashed rounded-xl" data-testid="empty-state">
      {Icon && <Icon className="text-mauve mx-auto mb-3" size={22} strokeWidth={1.4} />}
      <p className="font-heading text-lg text-cocoa">{title}</p>
      <p className="mt-2 text-sm text-cocoa/65 max-w-sm mx-auto leading-relaxed">{body}</p>
      {ctaText && ctaTo && (
        <Link to={ctaTo} className="inline-block mt-4"><button className="ss-pill ss-pill-primary h-10">{ctaText}</button></Link>
      )}
    </div>
  );
}

// ─── ASK SUGARSENSE ────────────────────────────────────────────────────────
const GENERAL_SCOPE = "__general__";
const LEGACY_ASK_DRAFT_KEY = "ss_ask_draft_v1";
const askDraftKey = (userId) => `ss_ask_draft_v2:${userId || "anon"}`;
export const clearAskDrafts = () => {
  try { Object.keys(localStorage).filter((k) => k.startsWith("ss_ask_draft")).forEach((k) => localStorage.removeItem(k)); } catch {}
};
const EMPTY_ASK_FORM = { what_happened: "", relationship_stage: "", user_goal: "", his_recent_behaviour: "", promises_made: "", follow_through: "", emotional_state: "", considering_next: "" };

export function Ask() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialProfile = params.get("profile_id") || "";
  const contextHint = params.get("context") || "";
  const [form, setForm] = useState(EMPTY_ASK_FORM);
  // profileId: "" = not chosen yet, GENERAL_SCOPE = general reflection, else a profile id
  const [profileId, setProfileId] = useState(initialProfile);
  const [profiles, setProfiles] = useState([]);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showContext, setShowContext] = useState(false);
  const [check, setCheck] = useState(null);          // balanced guidance context-check (bg-1.0)
  const [ccAnswers, setCcAnswers] = useState({});
  const [ccDone, setCcDone] = useState(false);
  const [restored, setRestored] = useState(null); // { alias } when a draft was restored
  const [pendingDraft, setPendingDraft] = useState(null); // draft about a different persona than the deep link
  const [processing, setProcessing] = useState(null); // { ingestion_id, parts_processed, parts_total, message } while a long entry is still being read (HTTP 202)
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeDetail, setUpgradeDetail] = useState(null);
  const draftKey = askDraftKey(user?.id);

  const aliasOf = (pid, list = profiles) => (pid === GENERAL_SCOPE ? "General reflection" : (list.find((p) => p.id === pid)?.alias || ""));
  const activeProfile = profiles.find((p) => p.id === profileId);
  const pn = pronounSet(activeProfile?.partner_pronouns, activeProfile?.alias, activeProfile?.custom_pronouns);

  // Load profiles + restore draft. Precedence (audit D5): URL ?profile_id= wins;
  // a draft about a different persona is offered, never silently applied.
  useEffect(() => {
    try { localStorage.removeItem(LEGACY_ASK_DRAFT_KEY); } catch {}
    api.get("/profiles").then((r) => {
      const list = r.data || [];
      setProfiles(list);
      setProfilesLoaded(true);
      let saved = null;
      try { const raw = localStorage.getItem(draftKey); if (raw) saved = JSON.parse(raw); } catch {}
      const hasDraft = saved && saved.form && saved.form.what_happened;
      if (hasDraft && initialProfile && saved.profileId && saved.profileId !== initialProfile) {
        setPendingDraft({ ...saved, alias: aliasOf(saved.profileId, list) });
      } else if (hasDraft) {
        setForm(saved.form);
        if (!initialProfile && saved.profileId) setProfileId(saved.profileId);
        if (Object.values(saved.form).slice(1).some(Boolean)) setShowContext(true);
        setRestored({ alias: aliasOf(initialProfile || saved.profileId, list) });
      }
      // Single profile → visible default (matches backend rule)
      if (!initialProfile && !(hasDraft && saved.profileId) && list.length === 1) setProfileId(list[0].id);
    }).catch(() => setProfilesLoaded(true));
    if (contextHint === "promise") {
      setShowContext(true);
      setForm((f) => ({ ...f, user_goal: f.user_goal || "Understand their follow-through" }));
    }
    // eslint-disable-next-line
  }, []);

  // Debounced autosave to localStorage (keyed by user)
  useEffect(() => {
    const t = setTimeout(() => {
      if (form.what_happened || Object.values(form).some(Boolean)) {
        try { localStorage.setItem(draftKey, JSON.stringify({ form, profileId, ts: Date.now() })); } catch {}
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form, profileId, draftKey]);

  const set = (k) => (e) => { if (k === "what_happened") { setCcDone(false); setCheck(null); } setForm({ ...form, [k]: e.target.value }); };

  const basePayload = () => {
    const payload = { ...form };
    if (profileId === GENERAL_SCOPE) payload.scope = "general";
    else if (profileId) payload.profile_id = profileId;
    const c = getSavedCountry(); if (c && c !== "XX") { payload.user_country = c; payload.country = c; }
    return payload;
  };

  const refreshSafetyCountry = async (c) => {
    try { const r = await api.post("/advisor/context-check", { ...basePayload(), country: c && c !== "XX" ? c : undefined }); setCheck((prev) => prev ? { ...prev, safety_support: r.data.safety_support } : prev); setResult((prev) => prev ? { ...prev, safety_support: r.data.safety_support } : prev); } catch {}
  };

  const runAsk = async (answers, skippedAll, ingestionId) => {
    setBusy(true); setErr("");
    const rid = (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, "");
    try {
      const payload = basePayload();
      payload.client_request_id = rid;
      if (ingestionId) payload.ingestion_id = ingestionId;
      const cc = { ...answers };
      if (skippedAll) { cc.continued_without_answers = true; cc.skipped = Array.from(new Set([...(cc.skipped || []), ...((check?.questions || []).map((q) => q.id))])); }
      if (Object.keys(cc).length) payload.context_check = cc;
      if (check?.questions?.length) payload.questions_shown = check.questions.map((q) => q.id);
      const r = await api.post("/advisor/ask", payload);
      if (r.status === 202 || r.data?.detail?.code === "entry_still_processing") {
        // Truthful "still reading" state: keep the member's text and the ingestion identity so the same entry resumes.
        const d = r.data?.detail || {};
        setProcessing({ ingestion_id: d.ingestion_id, parts_processed: d.parts_processed, parts_total: d.parts_total, message: d.message });
        setErr("");
        return;
      }
      setProcessing(null);
      setResult(r.data); setCcDone(true); setCheck(null);
      if (r.data?.profile_id && r.data.profile_id !== profileId) setProfileId(r.data.profile_id);
      try { localStorage.removeItem(draftKey); } catch {}
      setRestored(null);
      setTimeout(() => document.querySelector('[data-testid="ask-result"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      const st = e2?.response?.status;
      if (st === 402 && detail && typeof detail === "object" && detail.code === "advisor_capacity_reached") {
        setUpgradeDetail(detail); setShowUpgrade(true);
      } else if (!st || st === 502 || st === 504) {
        // The entry was saved before the read started — recover the result instead of asking again.
        setErr("Still reading your entry…");
        let recovered = false;
        for (let i = 0; i < 25 && !recovered; i++) {
          await new Promise((res) => setTimeout(res, 3000));
          try {
            const rr = await api.get(`/advisor/by-request/${rid}`);
            if (rr.data?.status && rr.data.status !== "pending" && rr.data.result) {
              setResult(rr.data); setCcDone(true); setCheck(null); setErr(""); recovered = true;
              try { localStorage.removeItem(draftKey); } catch {}
              setTimeout(() => document.querySelector('[data-testid="ask-result"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
            }
          } catch {}
        }
        if (!recovered) setErr("That took longer than usual. Your entry is saved in the box — please try once more.");
      } else if (st === 503 && detail && typeof detail === "object" && detail.code === "entry_processing_failed" && detail.ingestion_id) {
        setProcessing({ ingestion_id: detail.ingestion_id, message: detail.message, failed: true });
        setErr("");
      } else {
        setErr(typeof detail === "string" ? detail : (detail?.message || "Couldn't process — try again."));
      }
    } finally { setBusy(false); }
  };

  const clearDraft = () => {
    setForm(EMPTY_ASK_FORM);
    setProfileId(initialProfile || (profiles.length === 1 ? profiles[0].id : ""));
    setRestored(null);
    setPendingDraft(null);
    try { localStorage.removeItem(draftKey); } catch {}
  };

  const restorePendingDraft = () => {
    if (!pendingDraft) return;
    setForm(pendingDraft.form);
    setProfileId(pendingDraft.profileId);
    setResult(null);
    if (Object.values(pendingDraft.form).slice(1).some(Boolean)) setShowContext(true);
    setRestored({ alias: pendingDraft.alias });
    setPendingDraft(null);
  };

  // Changing the active persona clears the previous result (audit D13)
  const changeProfile = (val) => { setProfileId(val); setResult(null); setErr(""); setCheck(null); setCcDone(false); };

  const needsChoice = profiles.length > 1 && !profileId;

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setResult(null);
    if (needsChoice) { setErr("Choose who this is about, or pick “General reflection”."); return; }
    // Balanced guidance: one rules-based pass (no model call) to see whether ≤2 optional
    // questions would materially change the advice. Never blocks — she can always continue.
    if (!ccDone && !check) {
      setBusy(true);
      try {
        const r = await api.post("/advisor/context-check", basePayload());
        if (r.data?.questions?.length) {
          setCheck(r.data); setCcAnswers({}); setBusy(false);
          setTimeout(() => document.querySelector('[data-testid="ask-context-check"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
          return;
        }
      } catch { /* never block guidance on the check */ }
    }
    await runAsk({}, false);
  };

  // "Before you reply" quick insight — one-shot, no session memory.
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickResult, setQuickResult] = useState(null);
  const [quickErr, setQuickErr] = useState("");
  const quickSubmit = async () => {
    if (!form.what_happened || form.what_happened.trim().length < 8) {
      setQuickErr("Add a few words of context first."); return;
    }
    setQuickBusy(true); setQuickErr(""); setQuickResult(null);
    try {
      const r = await api.post("/advisor/quick-insight", { what_happened: form.what_happened, profile_id: (profileId && profileId !== GENERAL_SCOPE) ? profileId : null });
      setQuickResult(r.data?.result);
      setTimeout(() => document.querySelector('[data-testid="quick-insight-result"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) { setQuickErr(e?.response?.data?.detail || "Couldn't generate a quick read."); }
    finally { setQuickBusy(false); }
  };

  return (
    <div data-testid="ask-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10 pb-32">
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} detail={upgradeDetail} />
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Ask SugarSense</p>
      <h1 className="font-heading text-4xl sm:text-5xl text-cocoa mt-1">
        Tell me what happened{user?.display_name ? <span className="text-cocoa/70">, {user.display_name}</span> : ""}.
      </h1>
      <p className="mt-3 text-cocoa/70 max-w-2xl">Write it the way you'd tell a friend. Your observations are enough — never paste their private messages.</p>

      {/* Privacy assurance — moved to top */}
      <div className="mt-5 ss-card p-4 flex gap-3 items-start bg-blush/15">
        <ShieldCheck className="text-mauve flex-shrink-0 mt-0.5" size={18} />
        <p className="text-sm text-cocoa/80">
          Discreet, encrypted at rest, yours to delete at any time. No real names. Use aliases.
        </p>
      </div>

      {pendingDraft && (
        <div data-testid="ask-pending-draft" className="mt-3 ss-card p-4 bg-blush/15 text-sm text-cocoa/80 flex flex-wrap items-center gap-3">
          <span>You have an unsent draft about <strong>{pendingDraft.alias || "someone else"}</strong>. Restoring it will switch this read to {pendingDraft.alias || "that person"}.</span>
          <button type="button" onClick={restorePendingDraft} data-testid="ask-pending-restore" className="text-mauve underline text-xs">Restore &amp; switch</button>
          <button type="button" onClick={() => { setPendingDraft(null); try { localStorage.removeItem(draftKey); } catch {} }} data-testid="ask-pending-discard" className="text-cocoa/60 underline text-xs">Discard draft</button>
        </div>
      )}
      {restored && (
        <div data-testid="ask-restored" className="mt-3 text-sm text-cocoa/65 flex items-center gap-3">
          <span>Picked up where you left off{restored.alias ? <> — about <strong className="text-cocoa">{restored.alias}</strong></> : ""}.</span>
          <button onClick={clearDraft} className="text-mauve underline text-xs">Start fresh</button>
        </div>
      )}

      <form onSubmit={submit} className="mt-6 ss-card p-6 sm:p-8 space-y-4" data-testid="ask-form">
        {profiles.length > 0 && (
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-cocoa/50">Who is this about?</span>
            <select value={profileId} onChange={(e) => changeProfile(e.target.value)} data-testid="ask-profile-select"
              className={`mt-1 w-full bg-ivory border rounded-md h-11 px-3 text-cocoa ${needsChoice ? "border-mauve" : "border-champagne"}`}>
              {profiles.length > 1 && <option value="">— Choose a person —</option>}
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.alias}{p.partner_pronouns && p.partner_pronouns !== "unknown" ? ` (${p.partner_pronouns === "other" && p.custom_pronouns ? p.custom_pronouns : p.partner_pronouns})` : ""}</option>)}
              <option value={GENERAL_SCOPE}>General reflection — not about a specific person</option>
            </select>
            {activeProfile && <span data-testid="ask-active-persona" className="mt-1 block text-xs text-cocoa/55">Reading about <strong className="text-cocoa">{activeProfile.alias}</strong>{profiles.length === 1 && !initialProfile ? " (your only saved person)" : ""}. Pronouns: {pn.label}.</span>}
            {profileId === GENERAL_SCOPE && <span data-testid="ask-active-persona" className="mt-1 block text-xs text-cocoa/55">Kept as a general reflection — nothing is filed against a saved person.</span>}
            {needsChoice && <span data-testid="ask-choose-hint" className="mt-1 block text-xs text-mauve">Choose who this is about, or pick “General reflection”.</span>}
          </label>
        )}
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-cocoa/50">What happened?</span>
          <textarea data-testid="ask-what-happened" value={form.what_happened} onChange={set("what_happened")} required minLength={4} maxLength={20000}
            rows={6} placeholder="Tell it the way you'd tell a friend over coffee. The texture matters."
            className="mt-1 w-full bg-ivory border border-champagne rounded-md p-3 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none leading-relaxed" />
          {form.what_happened.length > 1800 && (
            <span data-testid="ask-length-hint" className="block mt-1 text-[11px] text-cocoa/50">{form.what_happened.length.toLocaleString()} characters · longer entries are read in parts, nothing is cut</span>
          )}
        </label>
        <VoiceInput testid="ask-voice" context={activeProfile?.alias} nudge={form.what_happened.length >= 280}
          onText={(t) => setForm((f) => ({ ...f, what_happened: [f.what_happened.trim(), t].filter(Boolean).join("\n\n").slice(0, 20000) }))} />

        {!showContext ? (
          <button type="button" onClick={() => setShowContext(true)} data-testid="ask-add-context"
            className="text-mauve text-sm hover:text-cocoa transition-colors">
            + Add context (optional — sharpens the read)
          </button>
        ) : (
          <div className="space-y-3 pt-2 border-t border-champagne" data-testid="ask-context">
            <p className="text-xs uppercase tracking-widest text-cocoa/50">Optional context — sharpen the read</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="ask-stage" value={form.relationship_stage} onChange={set("relationship_stage")} placeholder="Stage (talking, early, established…)" maxLength={40} className="bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
              <input data-testid="ask-goal" value={form.user_goal} onChange={set("user_goal")} placeholder="Your goal (clarity, slow down…)" maxLength={200} className="bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
              <input data-testid="ask-his-behaviour" value={form.his_recent_behaviour} onChange={set("his_recent_behaviour")} placeholder={`${pn.cap(pn.possessive)} recent behaviour`} maxLength={1000} className="bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
              <input data-testid="ask-promises" value={form.promises_made} onChange={set("promises_made")} placeholder={`Promises or commitments ${pn.subject} made`} maxLength={1000} className="bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
              <input data-testid="ask-followthrough" value={form.follow_through} onChange={set("follow_through")} placeholder="Follow-through so far" maxLength={1000} className="bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
              <input data-testid="ask-emotion" value={form.emotional_state} onChange={set("emotional_state")} placeholder="How you're feeling" maxLength={400} className="bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
            </div>
            <textarea data-testid="ask-considering" value={form.considering_next} onChange={set("considering_next")} rows={2} placeholder="What are you considering doing next?" maxLength={600} className="w-full bg-ivory border border-champagne rounded-md p-3 outline-none focus:border-mauve" />
          </div>
        )}

        {err && <div data-testid="ask-err" className="text-rose text-sm">{err}</div>}
        {processing && (
          <div data-testid="ask-processing" className="ss-card p-4 text-sm border-gold bg-champagne/30 flex flex-wrap items-center gap-3">
            <span className="text-cocoa/85">{processing.message || "Your entry is still being read — nothing was lost."}{processing.parts_total ? ` (${processing.parts_processed ?? 0} of ${processing.parts_total} parts read so far)` : ""}</span>
            <button type="button" data-testid="ask-processing-resume" disabled={busy} className="ss-pill ss-pill-primary h-9 text-xs"
              onClick={() => runAsk({}, false, processing.ingestion_id)}>Continue reading this entry</button>
          </div>
        )}
        <div className="flex gap-2 items-center flex-wrap">
          <button data-testid="ask-submit" type="submit" disabled={busy || !form.what_happened.trim()} className="ss-pill ss-pill-primary h-12 px-7 disabled:opacity-50">
            {busy ? "Reading the pattern…" : <><Sparkles size={14} /> Read the pattern</>}
          </button>
          <button
            type="button"
            data-testid="ask-quick-insight"
            disabled={quickBusy || busy || !form.what_happened.trim()}
            onClick={quickSubmit}
            className="ss-pill ss-pill-ghost h-12 px-5 disabled:opacity-50"
            title="Before you reply — a 30-second read with no session memory"
          >
            {quickBusy ? "Reading…" : "Before you reply"}
          </button>
          {(form.what_happened || showContext) && (
            <button type="button" onClick={clearDraft} className="text-xs text-cocoa/50 hover:text-cocoa">Clear</button>
          )}
        </div>
      </form>

      {check && !result && (
        <div className="mt-6 space-y-4">
          <SafetySupport support={check.safety_support} onCountry={refreshSafetyCountry} />
          <ContextCheck check={check} answers={ccAnswers} onChange={setCcAnswers} busy={busy}
            onAnswered={() => runAsk(ccAnswers, false)} onSkipAll={() => runAsk(ccAnswers, true)} />
        </div>
      )}

      {/* Quick insight result */}
      {quickErr && <div className="mt-4 ss-card p-3 text-rose text-sm">{quickErr}</div>}
      {quickResult && (
        <section data-testid="quick-insight-result" className="mt-6 ss-card-premium p-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Quick read · before you reply</p>
          <p className="mt-2 text-cocoa text-lg leading-relaxed">{quickResult.observation}</p>
          {quickResult.what_to_avoid && (
            <p className="mt-3 text-cocoa/70 text-sm italic">Avoid: {quickResult.what_to_avoid}</p>
          )}
          {quickResult.suggested_reply && (
            <div className="mt-4 ss-card p-4 bg-mauve/5">
              <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Suggested reply</p>
              <p className="mt-2 text-cocoa/85 italic leading-relaxed">"{quickResult.suggested_reply}"</p>
            </div>
          )}
          {quickResult.confidence && (
            <p className="mt-3 text-[10px] tracking-widest uppercase text-cocoa/45">Confidence: {quickResult.confidence}</p>
          )}
        </section>
      )}

      {result && (
        <>
          <SafetySupport support={result.safety_support} onCountry={refreshSafetyCountry} />
          {result.ingestion && (
            <div data-testid="ask-coverage" className={`mt-6 ss-card p-4 text-sm flex flex-wrap items-center gap-3 ${result.ingestion.parts_processed < result.ingestion.parts_total ? "border-gold bg-champagne/30" : "border-champagne"}`}>
              <span className="text-cocoa/80">
                {result.ingestion.parts_processed < result.ingestion.parts_total
                  ? `This read is based on ${result.ingestion.parts_processed} of ${result.ingestion.parts_total} parts of your entry — part of it couldn't be read yet, so treat the read as provisional.`
                  : `Your full entry was read in ${result.ingestion.parts_total} parts.`}
              </span>
              {result.ingestion.parts_processed < result.ingestion.parts_total && (
                <button type="button" data-testid="ask-coverage-retry" disabled={busy} className="ss-pill ss-pill-ghost h-9 text-xs"
                  onClick={() => runAsk({}, false, result.ingestion.id)}>Read the rest</button>
              )}
            </div>
          )}
          {result.output_status === "incomplete" && (
            <div data-testid="ask-incomplete" className="mt-6 ss-card p-4 text-sm border-gold bg-champagne/30 text-cocoa/80">
              The advisor couldn't finish this read. Your entry is saved; nothing from this attempt has been added to your history. Please try again in a moment.
            </div>
          )}
          <AdvisorResult data={result.result} profileId={result.profile_id} />
          {result.output_status !== "clarification" && (<>
          <ContextSuggestions suggestions={result.context_suggestions} profileId={result.profile_id} />
          <CorrectAdvisor sessionId={result.session_id} />
          <RecommendationOutcomes sessionId={result.session_id} />
          <div className="mt-6"><AdvisorFeedback sessionId={result.session_id} /></div>
          <p className="mt-4 text-xs text-cocoa/55" data-testid="ask-saved-note">Saved to your <Link to={`/history/advisor/${result.session_id}`} className="underline text-mauve">history</Link> — you can reopen this read any time.</p>
          </>)}
          {/* Mobile sticky action bar */}
          <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-ivory/95 backdrop-blur-md border-t border-champagne p-3 flex gap-2" data-testid="ask-sticky-bar">
            {result.result?.message_draft && (
              <button onClick={() => navigator.clipboard.writeText(result.result.message_draft)} className="ss-pill ss-pill-ghost h-10 flex-1">Copy draft</button>
            )}
            <button onClick={() => { setResult(null); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="ss-pill ss-pill-primary h-10 flex-1">Ask again</button>
          </div>
        </>
      )}
    </div>
  );
}

export function AdvisorResult({ data, profileId }) {
  if (!data) return null;
  if (data.clarification_needed?.kind === "persona_conflict") {
    // Clarification only (ha-1.2): no analysis, draft or actions until the member resolves which person this is about.
    return (
      <div className="mt-6 ss-card p-5 text-sm text-cocoa/85 border-gold bg-champagne/30" data-testid="ask-scope-conflict">
        <p className="text-[10px] tracking-[0.3em] uppercase text-mauve mb-2">Which person is this about?</p>
        <p className="leading-relaxed">{data.clarification_needed.message}</p>
        <p className="mt-2 text-xs text-cocoa/60">No read was produced and nothing was saved to {data.clarification_needed.selected}'s history.</p>
      </div>
    );
  }
  // Defensive cleanup: if any text field begins with a `{` and contains a
  // `"key":` pattern, it's stray JSON that escaped the backend. Hide it
  // rather than dump a code blob onto the user. The "Next step" CTA below
  // gives them a clear way forward.
  const looksLikeJson = (v) => typeof v === "string" && /^\s*\{[\s\S]*"\w+"\s*:/.test(v.trim());
  data = { ...data };
  ["what_stands_out", "likely_dynamic", "emotional_context", "beneath_the_surface",
   "suggested_approach", "what_to_avoid", "message_draft"].forEach((k) => {
    if (looksLikeJson(data[k])) data[k] = "";
  });
  const looksThin = !data.what_stands_out && !data.likely_dynamic && !data.suggested_approach && !data.message_draft;

  const PHASE_LABEL = {
    discovery: "Discovery", pre_meeting: "Pre-meeting", early_arrangement: "Early arrangement",
    escalation: "Escalation", consistency: "Consistency", cooling: "Cooling",
    conflict: "Conflict", re_engagement: "Re-engagement", ending: "Ending",
  };
  return (
    <div data-testid="ask-result" className="mt-8 space-y-5">
      {data.safety_note && (
        <div data-testid="ask-safety" className="ss-card p-4 border-rose bg-blush/40">
          <div className="flex gap-3"><AlertCircle className="text-rose flex-shrink-0 mt-0.5" size={18} /><p className="text-cocoa text-sm">{data.safety_note}</p></div>
        </div>
      )}
      {/* Header banner */}
      <div className="ss-card-premium p-5 sm:p-6 flex flex-wrap gap-2 items-center">
        {data.archetype?.name && <Pill color="mauve">{data.archetype.name}</Pill>}
        {data.phase_detected && <Pill>Phase: {PHASE_LABEL[data.phase_detected] || data.phase_detected}</Pill>}
        {data.confidence && <Pill color="sage">Confidence: {data.confidence}</Pill>}
        {data.suggested_tone && <Pill>Tone: {TONE_LABEL[data.suggested_tone] || data.suggested_tone}</Pill>}
      </div>

      {data.what_stands_out && (
        <div className="ss-card-premium p-5 sm:p-6">
          <SectionHead icon={Sparkles}>What stands out</SectionHead>
          <Maybe className="block mt-2 text-cocoa text-[15px] sm:text-base leading-relaxed">{data.what_stands_out}</Maybe>
        </div>
      )}

      {data.likely_dynamic && (
        <div className="ss-card-premium p-5 sm:p-6">
          <SectionHead icon={BookOpen}>The likely dynamic</SectionHead>
          <Maybe className="block mt-2 text-cocoa/90 leading-relaxed">{data.likely_dynamic}</Maybe>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {data.green_signals?.length > 0 && (
          <div className="ss-card p-5">
            <SectionHead icon={Check} colorClass="text-sage">Green signals</SectionHead>
            <ul className="mt-2 space-y-2">
              {data.green_signals.map((s, i) => <li key={i} className="text-sm text-cocoa/85 flex gap-2"><span className="text-sage mt-0.5">·</span><Maybe>{s}</Maybe></li>)}
            </ul>
          </div>
        )}
        {data.areas_to_watch?.length > 0 && (
          <div className="ss-card p-5">
            <SectionHead icon={Eye} colorClass="text-rose">Areas to watch</SectionHead>
            <ul className="mt-2 space-y-2">
              {data.areas_to_watch.map((s, i) => <li key={i} className="text-sm text-cocoa/85 flex gap-2"><span className="text-rose mt-0.5">·</span><Maybe>{s}</Maybe></li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {data.emotional_context && (
          <div className="ss-card p-5">
            <SectionHead icon={Heart}>Emotional context</SectionHead>
            <Maybe className="block mt-2 text-cocoa/85 text-sm leading-relaxed">{data.emotional_context}</Maybe>
          </div>
        )}
        {data.beneath_the_surface && (
          <div className="ss-card p-5">
            <SectionHead icon={EyeOff}>Beneath the surface</SectionHead>
            <Maybe className="block mt-2 text-cocoa/85 text-sm leading-relaxed">{data.beneath_the_surface}</Maybe>
          </div>
        )}
      </div>

      {data.suggested_approach && (
        <div className="ss-card-premium p-5 sm:p-6">
          <SectionHead icon={Send} colorClass="text-mauve">Suggested approach</SectionHead>
          <Maybe className="block mt-2 text-cocoa font-medium leading-relaxed">{data.suggested_approach}</Maybe>
        </div>
      )}

      <BalancedView b={data.balanced} />

      {data.what_to_avoid && (
        <div className="ss-card p-5">
          <SectionHead icon={X} colorClass="text-rose">What to avoid</SectionHead>
          <Maybe className="block mt-2 text-cocoa/85 text-sm leading-relaxed">{data.what_to_avoid}</Maybe>
        </div>
      )}

      {data.draft_suppressed === "safety_mode" && (
        <div className="ss-card p-4 text-sm text-cocoa/75 border-champagne" data-testid="ask-draft-suppressed">
          No message draft with this read — when safety is in question we don't suggest words to send to the other person. The support options above are yours to use whenever you choose.
        </div>
      )}

      {data.message_draft && (
        <div className="ss-card-premium p-5 sm:p-6 bg-blush/25" data-testid="ask-message-draft">
          <SectionHead icon={Send} colorClass="text-mauve">Draft you could send</SectionHead>
          {data.draft_caution && <p className="mt-2 text-xs text-rose/90" data-testid="ask-draft-caution">{data.draft_caution}</p>}
          <Maybe className="block mt-3 text-cocoa text-[15px] sm:text-base leading-relaxed italic">"{data.message_draft}"</Maybe>
          <button onClick={() => navigator.clipboard.writeText(data.message_draft)} data-testid="ask-copy-draft"
            className="mt-4 ss-pill ss-pill-ghost h-9 text-xs">Copy to clipboard</button>
        </div>
      )}

      {/* "What now?" footer — always visible so the user is never stranded */}
      <div className="ss-card-premium p-5 sm:p-6" data-testid="ask-next-steps">
        <SectionHead icon={ArrowRight} colorClass="text-mauve">What next</SectionHead>
        {looksThin ? (
          <div className="mt-3 space-y-3">
            <p className="text-cocoa/80 text-sm leading-relaxed">
              That read came back thinner than we'd like. A little more context tends to help —
              try one of these and ask again. Your free read has not been counted.
            </p>
            <ul className="text-sm text-cocoa/75 space-y-1.5 list-disc list-inside">
              <li>What did this person say or do — paste the exact words if you have them</li>
              <li>How long you've known this person, and roughly what stage you're at</li>
              <li>Your goal — clarity, distance, a step forward, or something else</li>
            </ul>
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="ss-pill ss-pill-primary h-10 text-sm">
              Refine and ask again
            </button>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              data-testid="next-ask-again" className="ss-pill ss-pill-primary h-10 text-sm">
              Ask a follow-up
            </button>
            {data.archetype?.key && data.archetype.key !== "unclear" && (
              <Link to={`/archetypes/${data.archetype.key}${profileId ? `?profile_id=${profileId}` : ""}`}>
                <button data-testid="next-see-archetype" className="ss-pill ss-pill-ghost h-10 text-sm">
                  Read the full {data.archetype.name || "archetype"}
                </button>
              </Link>
            )}
            <Link to="/dashboard">
              <button data-testid="next-dashboard" className="ss-pill ss-pill-ghost h-10 text-sm">
                Back to dashboard
              </button>
            </Link>
            <Link to="/essays">
              <button data-testid="next-essays" className="ss-pill ss-pill-ghost h-10 text-sm">
                Related editorial
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHead({ icon: Icon, children, colorClass = "text-mauve" }) {
  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className={colorClass} />}
      <p className={`text-[11px] tracking-[0.35em] uppercase ${colorClass}`}>{children}</p>
    </div>
  );
}

// ─── PROFILES LIST ─────────────────────────────────────────────────────────
export function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [usage, setUsage] = useState(null);
  const load = () => api.get("/profiles").then((r) => setProfiles(r.data || []));
  useEffect(() => { load(); api.get("/advisor/usage").then((r) => setUsage(r.data)); }, []);
  return (
    <div data-testid="profiles-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Profiles</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-cocoa">The people you're reading.</h1>
          <p className="mt-2 text-cocoa/70 text-sm">Alias-only. Never identifying. Encrypted.</p>
        </div>
        <button data-testid="profiles-new-btn" onClick={() => setShowNew(true)} className="ss-pill ss-pill-gold h-11 self-start sm:self-auto shrink-0"><Plus size={14} /> New profile</button>
      </div>
      {showNew && <NewProfileForm onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {profiles.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="No reads yet."
          body="Start by adding the person you're navigating — alias only, no real names. SugarSense reads the pattern as you observe."
          ctaText="Add your first profile"
          ctaTo="#"
        />
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          {profiles.map((p) => (
            <div key={p.id} data-testid={`profile-card-${p.id}`} className="ss-card-premium p-6 group transition-shadow hover:shadow-md">
              <Link to={`/profiles/${p.id}`} className="block">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <Maybe className="font-heading text-2xl text-cocoa group-hover:text-mauve transition-colors">{p.alias}</Maybe>
                    <p className="text-xs uppercase tracking-widest text-cocoa/50 mt-1">{STAGE_LABEL[p.stage] || "—"}</p>
                  </div>
                  {p.archetype_key && <Pill color="mauve">{p.archetype_key.replace(/_/g, " ")}</Pill>}
                </div>
                <Maybe className="block mt-4 text-sm text-cocoa/70 line-clamp-2">{p.notes}</Maybe>
                <div className="mt-4 flex gap-2 flex-wrap text-xs text-cocoa/60">
                  {p.city && <span>{p.city}</span>}
                  {p.occupation_category && <span>· {p.occupation_category}</span>}
                </div>
              </Link>
              <div className="mt-5 pt-4 border-t border-champagne/70 flex flex-wrap gap-2">
                <Link to={`/ask?profile_id=${p.id}`} data-testid={`profile-card-${p.id}-ask`} className="max-w-full">
                  <button className="ss-pill ss-pill-gold h-9 max-w-full"><Sparkles size={12} className="shrink-0" /><span className="truncate">Ask about {p.alias}</span></button>
                </Link>
                <Link to={`/profiles/${p.id}`}><button className="ss-pill ss-pill-ghost h-9">Open</button></Link>
              </div>
            </div>
          ))}
        </div>
      )}
      {usage?.plan === "free" && profiles.length >= 1 && (
        <p className="mt-8 text-xs text-cocoa/50">Free plan supports 1 profile. <Link to="/billing" className="underline">Upgrade</Link> for more.</p>
      )}
    </div>
  );
}

const PRONOUN_OPTIONS = [["unknown", "Pronouns — not sure yet"], ["she/her", "she/her"], ["he/him", "he/him"], ["they/them", "they/them"], ["other", "other"]];
const STRUCTURE_OPTIONS = [["unsure", "Structure — still unfolding"], ["monogamous", "Monogamous"], ["enm", "Ethically non-monogamous"], ["poly", "Polyamorous"]];

function NewProfileForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ alias: "", stage: "early", city: "", occupation_category: "", notes: "", partner_pronouns: "unknown", custom_pronouns: "", relationship_structure: "unsure" });
  const [err, setErr] = useState("");
  const [limitHit, setLimitHit] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/profiles", form); onCreated(); }
    catch (e2) {
      const d = e2?.response?.data?.detail;
      if (e2?.response?.status === 402) { setLimitHit(true); setErr(d?.message || "Your plan's profile limit is reached."); }
      else setErr((typeof d === "string" ? d : d?.message) || "Couldn't create");
    }
  };
  const sel = "bg-ivory border border-champagne rounded-md h-11 px-3";
  return (
    <form onSubmit={submit} data-testid="new-profile-form" className="mt-6 ss-card p-6 space-y-3">
      <input data-testid="new-profile-alias" value={form.alias} onChange={(e) => setForm({...form, alias: e.target.value})} required minLength={1} maxLength={40} placeholder="Alias (no real name)" className="w-full bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
      <div className="grid sm:grid-cols-2 gap-3">
        <select value={form.stage} onChange={(e) => setForm({...form, stage: e.target.value})} className={sel}>
          {Object.entries(STAGE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} placeholder="City (optional)" maxLength={80} className={`${sel} outline-none focus:border-mauve`} />
        <select data-testid="new-profile-pronouns" value={form.partner_pronouns} onChange={(e) => setForm({...form, partner_pronouns: e.target.value})} className={sel}>
          {PRONOUN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {form.partner_pronouns === "other" && (
          <input data-testid="new-profile-custom-pronouns" value={form.custom_pronouns} maxLength={40} onChange={(e) => setForm({...form, custom_pronouns: e.target.value})}
            placeholder="Their pronouns exactly, e.g. ze/hir or xe/xem/xyr" className={`${sel} outline-none`} />
        )}
        <select data-testid="new-profile-structure" value={form.relationship_structure} onChange={(e) => setForm({...form, relationship_structure: e.target.value})} className={sel}>
          {STRUCTURE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={form.occupation_category} onChange={(e) => setForm({...form, occupation_category: e.target.value})} placeholder="Occupation category (optional)" maxLength={80} className={`${sel} outline-none focus:border-mauve sm:col-span-2`} />
      </div>
      <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} placeholder="Notes — your observations only" maxLength={2000} className="w-full bg-ivory border border-champagne rounded-md p-3 outline-none focus:border-mauve" />
      {err && (
        <div data-testid="new-profile-error" className="text-rose text-sm">
          {err}{limitHit && <> <Link to="/billing" className="underline text-mauve" data-testid="new-profile-upgrade-link">See plans that include more profiles</Link></>}
        </div>
      )}
      <div className="flex gap-2">
        <button data-testid="new-profile-submit" type="submit" className="ss-pill ss-pill-primary h-11">Save profile</button>
        <button type="button" onClick={onClose} className="ss-pill ss-pill-ghost h-11">Cancel</button>
      </div>
      <p className="text-xs text-cocoa/40">Privacy reminder: alias only, no real names, addresses, or identifying photos. Your notes are encrypted and yours to delete.</p>
    </form>
  );
}

// ─── PROFILE DETAIL ───────────────────────────────────────────────────────
function ProfileIdentityEditor({ profile, onSaved }) {
  const [form, setForm] = useState({ stage: profile.stage || "early", partner_pronouns: profile.partner_pronouns || "unknown", custom_pronouns: profile.custom_pronouns || "", relationship_structure: profile.relationship_structure || "unsure" });
  const [state, setState] = useState("");
  const dirty = form.stage !== (profile.stage || "early") || form.partner_pronouns !== (profile.partner_pronouns || "unknown") || form.custom_pronouns !== (profile.custom_pronouns || "") || form.relationship_structure !== (profile.relationship_structure || "unsure");
  const save = async () => {
    setState("saving");
    try { const r = await api.put(`/profiles/${profile.id}`, form); onSaved(r.data); setState("saved"); setTimeout(() => setState(""), 1800); }
    catch { setState("error"); }
  };
  const sel = "bg-ivory border border-champagne rounded-md h-10 px-3 text-sm";
  return (
    <div className="mt-6 ss-card p-5" data-testid="profile-identity-editor">
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Who they are to you</p>
      <p className="mt-1 text-xs text-cocoa/55">These shape which essays and readings SugarSense surfaces — change them any time.</p>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <select data-testid="profile-edit-stage" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={sel}>
          {Object.entries(STAGE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="profile-edit-pronouns" value={form.partner_pronouns} onChange={(e) => setForm({ ...form, partner_pronouns: e.target.value })} className={sel}>
          {PRONOUN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {form.partner_pronouns === "other" && (
          <input data-testid="profile-edit-custom-pronouns" value={form.custom_pronouns} maxLength={40} onChange={(e) => setForm({ ...form, custom_pronouns: e.target.value })}
            placeholder="Their pronouns exactly, e.g. ze/hir" className={sel} />
        )}
        <select data-testid="profile-edit-structure" value={form.relationship_structure} onChange={(e) => setForm({ ...form, relationship_structure: e.target.value })} className={sel}>
          {STRUCTURE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button data-testid="profile-edit-save" onClick={save} disabled={!dirty || state === "saving"} className="ss-pill ss-pill-primary h-10 disabled:opacity-40">
          {state === "saving" ? "Saving…" : "Save"}
        </button>
        {state === "saved" && <span className="text-xs text-sage" data-testid="profile-edit-saved">Saved</span>}
        {state === "error" && <span className="text-xs text-rose" data-testid="profile-edit-error">Couldn't save — try again</span>}
      </div>
    </div>
  );
}

export function ProfileDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLimit, setReportLimit] = useState(null);
  const [gifts, setGifts] = useState([]);
  useEffect(() => {
    api.get(`/profiles/${id}`).then((r) => setProfile(r.data)).catch(() => nav("/profiles"));
    api.get(`/reports/${id}`, { quiet402: true }).then((r) => setReport(r.data))
      .catch((e) => { if (e?.response?.status === 402) setReportLimit(e.response.data?.detail || {}); });
    api.get(`/gifts?profile_id=${id}`).then((r) => setGifts(r.data || []));
  }, [id, nav]);
  const del = async () => {
    if (!window.confirm("Delete this profile and all linked gifts/timeline events?")) return;
    await api.delete(`/profiles/${id}`); nav("/profiles");
  };
  if (!profile) return <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 text-cocoa/50">Loading…</div>;
  const archetype = report?.archetype;
  return (
    <div data-testid="profile-detail-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/profiles" className="text-sm text-mauve flex items-center gap-1"><ArrowLeft size={14} /> All profiles</Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Maybe className="block font-heading text-5xl text-cocoa">{profile.alias}</Maybe>
          <div className="mt-2 flex flex-wrap gap-2">
            <Pill>{STAGE_LABEL[profile.stage] || "—"}</Pill>
            {profile.archetype_key && <Pill color="mauve">{profile.archetype_key.replace(/_/g, " ")}</Pill>}
            {report?.risk_level && <Pill color={report.risk_level === "high" ? "rose" : "sage"}>Risk: {report.risk_level}</Pill>}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/ask?profile_id=${id}`}><button data-testid="profile-ask-btn" className="ss-pill ss-pill-gold h-11"><Sparkles size={14} /> Ask about {profile.alias}</button></Link>
          <button onClick={del} data-testid="profile-delete-btn" className="ss-pill ss-pill-ghost h-11 text-rose"><Trash2 size={14} /></button>
        </div>
      </div>

      <ProfileIdentityEditor profile={profile} onSaved={setProfile} />
      <ProfileContextMemory profile={profile} onSaved={setProfile} />

      <PersonaTimeline profileId={profile.id} />

      {reportLimit && !report && (
        <div className="mt-6 ss-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="profile-report-locked">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Pattern report</p>
            <p className="mt-1 text-sm text-cocoa/70">Pattern reports read {profile.alias}'s consistency, follow-through and archetype over time. They're included from the Lite plan.</p>
          </div>
          <Link to="/billing"><button className="ss-pill ss-pill-gold h-10" data-testid="profile-report-upgrade">See plans</button></Link>
        </div>
      )}

      {archetype && (
        <div className="mt-6 ss-card p-6">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Likely archetype</p>
          <h2 className="font-heading text-2xl text-cocoa mt-1">{archetype.name}</h2>
          <p className="mt-2 text-cocoa/80 text-sm leading-relaxed">{archetype.description}</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div><p className="uppercase text-xs tracking-widest text-cocoa/50">What works</p><ul className="mt-1 space-y-1">{archetype.what_works.map((x, i) => <li key={i} className="text-cocoa/80">· {x}</li>)}</ul></div>
            <div><p className="uppercase text-xs tracking-widest text-cocoa/50">What to avoid</p><ul className="mt-1 space-y-1">{archetype.what_to_avoid.map((x, i) => <li key={i} className="text-cocoa/80">· {x}</li>)}</ul></div>
          </div>
          <p className="mt-4 text-sm italic text-mauve">Boundary: {archetype.boundary}</p>
        </div>
      )}

      {report && (
        <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="profile-stats">
          {Object.entries({"Events": report.stats.events_total, "Gifts": report.stats.gifts_total, "Promises kept": `${report.stats.promises_kept}/${report.stats.promises_made}`, "Follow-through": report.stats.follow_through_pct != null ? `${report.stats.follow_through_pct}%` : "—"}).map(([k, v]) => (
            <div key={k} className="ss-card p-4">
              <p className="text-xs uppercase tracking-widest text-cocoa/50">{k}</p>
              <Maybe className="block font-heading text-2xl text-cocoa mt-1">{v}</Maybe>
            </div>
          ))}
        </div>
      )}

      {report && (
        <div className="mt-6 ss-card p-6">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Suggested focus this week</p>
          <p className="mt-2 text-cocoa/85 italic">{report.suggested_focus}</p>
          <p className="mt-3 text-sm text-cocoa/60">Next 7 days: {report.next_7_days}</p>
        </div>
      )}

      <div className="mt-8">
        <h3 className="font-heading text-2xl text-cocoa">Recent activity</h3>
        {gifts.length === 0 ? <p className="mt-3 text-cocoa/60 text-sm">Nothing logged yet. <Link to="/tracker" className="text-mauve underline">Add a gift or support entry</Link>.</p> : (
          <ul className="mt-3 space-y-2">{gifts.slice(0, 6).map((g) => (
            <li key={g.id} className="ss-card p-3 flex items-center justify-between gap-3 text-sm">
              <Maybe className="font-medium text-cocoa">{g.label || g.type}</Maybe>
              <span className="text-cocoa/50">{g.occurred_on}</span>
            </li>
          ))}</ul>
        )}
      </div>
    </div>
  );
}

// ─── ARCHETYPES BROWSE ────────────────────────────────────────────────────
export function ArchetypeBook() {
  const [groups, setGroups] = useState({});
  const [cats, setCats] = useState({});
  const [open, setOpen] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  useEffect(() => {
    api.get("/archetypes/by-category").then((r) => {
      setGroups(r.data?.groups || {});
      setCats(r.data?.categories || {});
    });
  }, []);
  const allArchetypes = Object.values(groups).flat();
  const visible = activeCat === "all" ? allArchetypes : (groups[activeCat] || []);
  return (
    <div data-testid="archetypes-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <SeoHead
        title="Archetypes — Patterns, not labels"
        description="41 recurring relationship patterns across eight families — Future Faker, Consistent Patron, Lonely Provider, Status Romantic and more. Maps, not labels."
        path="/archetypes"
      />
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Archetypes</p>
      <h1 className="font-heading text-4xl sm:text-5xl text-cocoa">Patterns, not labels.</h1>
      <p className="mt-2 text-cocoa/70 max-w-2xl">{allArchetypes.length} recurring shapes across eight families. Useful as starting points — never the whole truth of any one person.</p>

      <div className="mt-6 sm:hidden" data-testid="archetype-cat-filters-mobile">
        <select value={activeCat} onChange={(e) => setActiveCat(e.target.value)}
          className="w-full bg-ivory border border-champagne rounded-full h-11 px-5 text-cocoa">
          <option value="all">All categories ({allArchetypes.length})</option>
          {Object.entries(cats).map(([k, label]) => (
            <option key={k} value={k}>{label} ({(groups[k] || []).length})</option>
          ))}
        </select>
      </div>
      <div className="mt-6 hidden sm:flex gap-2 flex-wrap" data-testid="archetype-cat-filters">
        <button onClick={() => setActiveCat("all")} className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest ${activeCat === "all" ? "bg-cocoa text-ivory" : "bg-champagne/50 text-cocoa/70"}`}>All ({allArchetypes.length})</button>
        {Object.entries(cats).map(([k, label]) => (
          <button key={k} onClick={() => setActiveCat(k)} data-testid={`archetype-cat-${k}`}
            className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest ${activeCat === k ? "bg-cocoa text-ivory" : "bg-champagne/50 text-cocoa/70"}`}>
            {label} ({(groups[k] || []).length})
          </button>
        ))}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((a) => (
          <button key={a.key} data-testid={`archetype-${a.key}`} onClick={() => setOpen(a)}
            className="ss-card-premium p-5 text-left hover:shadow-lg transition-all group">
            <div className="flex items-center gap-2 text-mauve mb-2">
              <BookOpen size={14} />
              <span className="text-[10px] tracking-[0.3em] uppercase">{cats[a.category] || a.category}</span>
            </div>
            <p className="font-heading text-xl text-cocoa group-hover:text-mauve transition-colors">{a.name}</p>
            <p className="mt-1 text-sm text-cocoa/70 line-clamp-2">{a.description}</p>
          </button>
        ))}
      </div>

      {open && (
        <div data-testid="archetype-modal" onClick={() => setOpen(null)} className="fixed inset-0 bg-cocoa/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="ss-card p-6 sm:p-7 max-w-2xl w-full max-h-[88vh] overflow-y-auto">
            <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{cats[open.category] || open.category}</p>
            <h2 className="font-heading text-2xl sm:text-3xl text-cocoa mt-1">{open.name}</h2>
            <p className="mt-3 text-cocoa/80 leading-relaxed">{open.description}</p>
            <div className="mt-5 space-y-4 text-sm">
              <Section title="Common signals" items={open.signals} />
              <Section title="Risks" items={open.risks} />
              <Section title="What works" items={open.what_works} />
              <Section title="What to avoid" items={open.what_to_avoid} />
              {open.progression && <SectionParagraph title="Typical progression" body={open.progression} />}
              {open.gifting && <SectionParagraph title="Gifting / support pattern" body={open.gifting} />}
              {open.conflict_style && <SectionParagraph title="Conflict style" body={open.conflict_style} />}
              {open.withdrawal_style && <SectionParagraph title="Withdrawal style" body={open.withdrawal_style} />}
              {open.emotional_needs?.length > 0 && <Section title="Emotional needs" items={open.emotional_needs} />}
              {open.emotional_triggers?.length > 0 && <Section title="Common triggers" items={open.emotional_triggers} />}
              <p className="italic text-mauve pt-2 border-t border-champagne">Healthy boundary — {open.boundary}</p>
            </div>
            <button onClick={() => setOpen(null)} className="ss-pill ss-pill-ghost h-10 mt-6">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
function Section({ title, items }) { if (!items?.length) return null; return <div><p className="uppercase text-xs tracking-widest text-cocoa/50 mb-1">{title}</p><ul className="space-y-1">{items.map((x, i) => <li key={i} className="text-cocoa/80">· {x}</li>)}</ul></div>; }
function SectionParagraph({ title, body }) { return <div><p className="uppercase text-xs tracking-widest text-cocoa/50 mb-1">{title}</p><p className="text-cocoa/80 text-sm leading-relaxed">{body}</p></div>; }

// ─── TIMELINE (across profiles) ───────────────────────────────────────────
export function Timeline() {
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/profiles").then((r) => { setProfiles(r.data || []); if (r.data?.[0]) setSelected(r.data[0].id); }); }, []);
  useEffect(() => { if (selected) api.get(`/timeline/${selected}`).then((r) => setEvents(r.data || [])); }, [selected]);
  return (
    <div data-testid="timeline-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-heading text-4xl text-cocoa">Timeline</h1>
      {profiles.length === 0 ? <EmptyState icon={Calendar} title="Create a profile first." body="The timeline builds itself once you have someone to track." ctaText="Add a profile" ctaTo="/profiles" /> : (
        <>
          <select value={selected || ""} onChange={(e) => setSelected(e.target.value)} data-testid="timeline-select"
            className="mt-4 bg-ivory border border-champagne rounded-md h-11 px-3">
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.alias}</option>)}
          </select>
          <ol className="mt-6 space-y-3">
            {events.length === 0 ? <EmptyState icon={Calendar} title="No events yet." body="Add a gift, a promise, or a milestone in the Tracker — they'll appear here as one story over time." ctaText="Open Tracker" ctaTo="/tracker" /> : events.map((e) => (
              <li key={e.id} data-testid={`timeline-event-${e.id}`} className="ss-card p-4 flex gap-4 items-start">
                <span className="text-mauve text-xs uppercase tracking-widest w-24 flex-shrink-0">{e.occurred_on}</span>
                <div className="flex-1">
                  <Pill>{e.kind}</Pill>
                  <Maybe className="block mt-1 text-cocoa/85 text-sm">{e.summary}</Maybe>
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

// ─── TRACKER ──────────────────────────────────────────────────────────────
export function Tracker() {
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ occurred_on: new Date().toISOString().slice(0, 10), type: "gift", label: "", value_estimate: "", currency: "USD", promised: false, delivered: true, emotional_context: "", notes: "" });
  const load = (pid) => Promise.all([
    api.get(`/gifts?profile_id=${pid}`).then((r) => setGifts(r.data || [])),
    api.get(`/gifts/analytics/${pid}`).then((r) => setAnalytics(r.data)),
  ]);
  useEffect(() => { api.get("/profiles").then((r) => { setProfiles(r.data || []); if (r.data?.[0]) { setSelected(r.data[0].id); load(r.data[0].id); } }); }, []);
  useEffect(() => { if (selected) load(selected); }, [selected]);
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, profile_id: selected, value_estimate: form.value_estimate ? parseFloat(form.value_estimate) : undefined };
    await api.post("/gifts", payload);
    setShow(false); load(selected);
  };
  return (
    <div data-testid="tracker-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl text-cocoa">Gift &amp; support tracker</h1>
          <p className="mt-2 text-cocoa/70 text-sm">Promises vs delivery. The pattern, quietly.</p>
        </div>
        <button data-testid="tracker-new" onClick={() => setShow((s) => !s)} className="ss-pill ss-pill-gold h-11">{show ? "Cancel" : <><Plus size={14} /> Log entry</>}</button>
      </div>
      {profiles.length === 0 ? <p className="mt-6 text-cocoa/60">Create a profile first.</p> : (
        <>
          <select value={selected || ""} onChange={(e) => setSelected(e.target.value)} className="mt-4 bg-ivory border border-champagne rounded-md h-11 px-3">
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.alias}</option>)}
          </select>
          {analytics && (
            <div className="mt-4 grid sm:grid-cols-4 gap-3">
              <div className="ss-card p-3"><p className="text-xs uppercase text-cocoa/50">Total</p><Maybe className="block font-heading text-xl text-cocoa">{analytics.total_events}</Maybe></div>
              <div className="ss-card p-3"><p className="text-xs uppercase text-cocoa/50">Promised</p><Maybe className="block font-heading text-xl text-cocoa">{analytics.promised_count}</Maybe></div>
              <div className="ss-card p-3"><p className="text-xs uppercase text-cocoa/50">Delivered</p><Maybe className="block font-heading text-xl text-cocoa">{analytics.delivered_count}</Maybe></div>
              <div className="ss-card p-3"><p className="text-xs uppercase text-cocoa/50">Follow-through</p><Maybe className="block font-heading text-xl text-cocoa">{analytics.follow_through_rate_pct != null ? `${analytics.follow_through_rate_pct}%` : "—"}</Maybe></div>
            </div>
          )}
          {show && (
            <form onSubmit={submit} className="mt-5 ss-card p-5 space-y-3" data-testid="tracker-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input type="date" value={form.occurred_on} onChange={(e) => setForm({...form, occurred_on: e.target.value})} className="bg-ivory border border-champagne rounded-md h-11 px-3" />
                <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="bg-ivory border border-champagne rounded-md h-11 px-3">
                  {["gift","support","experience","travel","other"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} placeholder="Label (e.g. monthly support)" maxLength={120} className="bg-ivory border border-champagne rounded-md h-11 px-3" />
                <input value={form.value_estimate} onChange={(e) => setForm({...form, value_estimate: e.target.value})} type="number" min="0" placeholder="Value (optional)" className="bg-ivory border border-champagne rounded-md h-11 px-3" />
              </div>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.promised} onChange={(e) => setForm({...form, promised: e.target.checked})} /> Was promised</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.delivered} onChange={(e) => setForm({...form, delivered: e.target.checked})} /> Delivered</label>
              </div>
              <textarea data-testid="tracker-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes (optional)" maxLength={1000} className="w-full bg-ivory border border-champagne rounded-md p-3" />
              <VoiceInput testid="tracker-voice" label="Speak a note"
                onText={(t) => setForm((f) => ({ ...f, notes: [f.notes.trim(), t].filter(Boolean).join(" ").slice(0, 1000) }))} />
              <button data-testid="tracker-submit" type="submit" className="ss-pill ss-pill-primary h-11">Save entry</button>
            </form>
          )}
          <ul className="mt-6 space-y-2">
            {gifts.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No entries yet."
                body="Log a gift, a promise, or a planned trip. Over time the follow-through pattern emerges quietly here."
              />
            ) : gifts.map((g) => (
              <li key={g.id} className="ss-card p-3 flex items-center justify-between text-sm">
                <div className="min-w-0 flex-1">
                  <Maybe className="font-medium text-cocoa">{g.label || g.type}</Maybe>
                  <p className="text-xs text-cocoa/50">{g.occurred_on} · {g.type}{g.promised ? " · promised" : ""}{!g.delivered ? " · not delivered" : ""}</p>
                </div>
                <Maybe className="text-cocoa/80">{g.value_estimate ? `${g.currency} ${g.value_estimate}` : ""}</Maybe>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ─── DRAFTS ───────────────────────────────────────────────────────────────
export function Drafts() {
  const [params] = useSearchParams();
  const [tone, setTone] = useState("soft");
  const [intent, setIntent] = useState("");
  const [profileId, setProfileId] = useState(params.get("profile_id") || "");
  const [profiles, setProfiles] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const load = () => api.get("/drafts").then((r) => setDrafts(r.data || []));
  useEffect(() => {
    load();
    api.get("/profiles").then((r) => setProfiles(r.data || []));
    if (params.get("intent") === "closure") setIntent("I'd like to close things between us with kindness.");
  }, [params]);
  const aliasOf = (id) => profiles.find((p) => p.id === id)?.alias || "—";
  const submit = async (e) => {
    e.preventDefault();
    if (intent.trim().length < 4) return;
    setBusy(true);
    try {
      const payload = { tone, intent };
      if (profileId) payload.profile_id = profileId;
      await api.post("/drafts", payload);
      setIntent(""); load();
    } catch {} finally { setBusy(false); }
  };
  const copy = (id, text) => { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 2000); };
  return (
    <div data-testid="drafts-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-heading text-4xl text-cocoa">Message drafts</h1>
      <p className="mt-2 text-cocoa/70">Choose a tone. Tell me what you want to say. Get an on-tone draft.</p>
      <form onSubmit={submit} className="mt-6 ss-card p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="drafts-tone" className="bg-ivory border border-champagne rounded-md h-11 px-3 w-full">
            {Object.entries(TONE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {profiles.length > 0 && (
            <select value={profileId} onChange={(e) => setProfileId(e.target.value)} data-testid="drafts-profile" className="bg-ivory border border-champagne rounded-md h-11 px-3 w-full">
              <option value="">— No specific profile —</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>For {p.alias}</option>)}
            </select>
          )}
        </div>
        <textarea data-testid="drafts-intent" value={intent} onChange={(e) => setIntent(e.target.value)} rows={3} minLength={4} maxLength={800} placeholder="What do you want to say?"
          className="w-full bg-ivory border border-champagne rounded-md p-3 outline-none focus:border-mauve" />
        <VoiceInput testid="drafts-voice" context={profiles.find((p) => p.id === profileId)?.alias}
          onText={(t) => setIntent((v) => [v.trim(), t].filter(Boolean).join(" ").slice(0, 800))} />
        <button data-testid="drafts-submit" disabled={busy} className="ss-pill ss-pill-primary h-11 disabled:opacity-50">{busy ? "Writing…" : <><Send size={14} /> Generate</>}</button>
      </form>
      {drafts.length === 0 ? (
        <EmptyState
          icon={Send}
          title="No drafts yet."
          body="Choose a tone above, write what you want to say, and SugarSense will give you a polished draft you can send."
        />
      ) : (
        <ul className="mt-8 space-y-3">
          {drafts.map((d) => (
            <li key={d.id} data-testid={`draft-${d.id}`} className="ss-card-premium p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <Pill color="rose">{TONE_LABEL[d.tone]}</Pill>
                {d.profile_id && <Pill>For {aliasOf(d.profile_id)}</Pill>}
                <span className="text-xs text-cocoa/50">{d.created_at?.slice(0,10)}</span>
              </div>
              {d.intent && <p className="mt-2 text-xs text-cocoa/55 italic">"{d.intent.slice(0, 120)}"</p>}
              <Maybe className="block mt-3 italic text-cocoa text-[15px] leading-relaxed">"{d.draft}"</Maybe>
              <div className="mt-3 flex gap-2 flex-wrap">
                <button onClick={() => copy(d.id, d.draft)} data-testid={`draft-${d.id}-copy`} className="ss-pill ss-pill-ghost h-9 text-xs">
                  {copied === d.id ? <><Check size={12} /> Copied</> : "Copy draft"}
                </button>
                <Link to={`/ask${d.profile_id ? `?profile_id=${d.profile_id}` : ""}`}>
                  <button className="ss-pill ss-pill-ghost h-9 text-xs"><Sparkles size={12} /> Refine with SugarSense</button>
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── ACCOUNT ──────────────────────────────────────────────────────────────
export function Account() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.display_name || "");
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState("");
  const [notifPrefs, setNotifPrefs] = useState({ weekly_digest_email: false, daily_checkin_email: false });
  const [notifMsg, setNotifMsg] = useState("");
  useEffect(() => { setNameValue(user?.display_name || ""); }, [user?.display_name]);
  useEffect(() => {
    api.get("/users/me/notification-prefs").then((r) => setNotifPrefs(r.data)).catch(() => {});
  }, []);
  const toggleNotif = async (key) => {
    const next = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(next);
    try {
      await api.put("/users/me/notification-prefs", { [key]: next[key] });
      setNotifMsg("Preferences updated.");
      setTimeout(() => setNotifMsg(""), 2500);
    } catch (e) { setNotifMsg(e?.response?.data?.detail || "Couldn't save."); }
  };
  const saveName = async () => {
    const v = nameValue.trim();
    if (!v) { setNameMsg("Tell me what to call you."); return; }
    setNameBusy(true); setNameMsg("");
    try {
      await api.put("/auth/me", { display_name: v });
      await refresh();
      setEditingName(false);
      setNameMsg("Saved.");
      setTimeout(() => setNameMsg(""), 2500);
    } catch (e) {
      setNameMsg(e?.response?.data?.detail || "Save failed.");
    } finally {
      setNameBusy(false);
    }
  };
  const exportData = async () => {
    const r = await api.get("/privacy/export");
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sugarsense-export.json"; a.click(); URL.revokeObjectURL(url);
  };
  const wipe = async () => {
    if (!window.confirm("Delete ALL your SugarSense data? This cannot be undone.")) return;
    setBusy(true); try { await api.delete("/privacy/all"); window.location.reload(); } finally { setBusy(false); }
  };
  return (
    <div data-testid="account-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-heading text-4xl text-cocoa">Account</h1>

      <div className="mt-6 ss-card p-6" data-testid="account-identity">
        <p className="text-xs uppercase tracking-widest text-cocoa/50">Signed in as</p>
        <p className="font-heading text-xl text-cocoa">{user?.name || user?.email}</p>
        <p className="text-sm text-cocoa/70">{user?.email}</p>
        <div className="mt-4"><Pill color="mauve">{PLAN_LABEL[user?.plan] || user?.tier_type || "free"}</Pill></div>

        <div className="mt-6 pt-5 border-t border-champagne">
          <p className="text-xs uppercase tracking-widest text-cocoa/50">How SugarSense addresses you</p>
          {!editingName ? (
            <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
              <p data-testid="display-name-value" className="font-heading text-2xl text-cocoa">
                {user?.display_name || <span className="text-cocoa/40 italic text-base">not set</span>}
              </p>
              <button data-testid="display-name-edit" onClick={() => setEditingName(true)} className="ss-pill ss-pill-ghost h-10 text-sm">Edit</button>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <input
                data-testid="display-name-account-input"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); }}
                maxLength={40}
                placeholder="e.g. Jane, J., Sage…"
                className="flex-1 min-w-[180px] bg-ivory border border-champagne rounded-md h-11 px-3 text-cocoa focus:border-mauve outline-none"
              />
              <button data-testid="display-name-account-save" disabled={nameBusy} onClick={saveName} className="ss-pill ss-pill-primary h-11 disabled:opacity-50">{nameBusy ? "Saving…" : "Save"}</button>
              <button onClick={() => { setEditingName(false); setNameValue(user?.display_name || ""); setNameMsg(""); }} className="ss-pill ss-pill-ghost h-11">Cancel</button>
            </div>
          )}
          {nameMsg && <p className="mt-2 text-xs text-cocoa/60">{nameMsg}</p>}
          <p className="mt-3 text-xs text-cocoa/55 leading-relaxed">
            This is the name your advisor will use in conversation. Private — never shared, never shown outside your own sessions.
          </p>
        </div>
      </div>

      <div className="mt-5 ss-card p-6">
        <h2 className="font-heading text-2xl text-cocoa">Notifications</h2>
        <p className="text-cocoa/70 text-sm mt-2">Two quiet emails. You can turn either on or off whenever.</p>
        <div className="mt-4 space-y-3" data-testid="notif-prefs">
          {[
            { k: "daily_checkin_email", l: "Daily 'Today's dynamic' email", d: "A single sentence, sent in the morning." },
            { k: "weekly_digest_email", l: "Weekly relationship summary", d: "Sundays — what shifted, what stood out." },
          ].map((opt) => (
            <label key={opt.k} className="flex items-start gap-3 p-3 rounded-md bg-champagne/20 cursor-pointer hover:bg-champagne/30 transition-colors">
              <input
                type="checkbox"
                data-testid={`notif-${opt.k}`}
                checked={!!notifPrefs[opt.k]}
                onChange={() => toggleNotif(opt.k)}
                className="mt-1 w-5 h-5 accent-mauve"
              />
              <div>
                <p className="text-cocoa font-heading text-base">{opt.l}</p>
                <p className="text-cocoa/55 text-xs mt-0.5">{opt.d}</p>
              </div>
            </label>
          ))}
        </div>
        {notifMsg && <p className="mt-3 text-xs text-cocoa/60">{notifMsg}</p>}
      </div>


      <div className="mt-5 ss-card p-6">
        <h2 className="font-heading text-2xl text-cocoa">Privacy</h2>
        <p className="text-cocoa/70 text-sm mt-2">Your data is yours. Export it, or delete everything in one click.</p>
        <div className="mt-4 flex gap-3 flex-wrap">
          <button data-testid="account-export" onClick={exportData} className="ss-pill ss-pill-ghost h-11"><Download size={14} /> Export my data</button>
          <button data-testid="account-wipe" disabled={busy} onClick={wipe} className="ss-pill h-11 bg-rose text-ivory hover:bg-mauve transition-colors disabled:opacity-50"><Trash2 size={14} /> Delete all my data</button>
        </div>
      </div>
      <div className="mt-5 ss-card p-6">
        <h2 className="font-heading text-2xl text-cocoa">Billing</h2>
        <p className="text-cocoa/70 text-sm mt-2">Change plan, view subscription, update payment.</p>
        <Link to="/billing"><button className="mt-4 ss-pill ss-pill-primary h-11">Manage billing</button></Link>
      </div>
    </div>
  );
}

// ─── BILLING — full Square lifecycle ──────────────────────────────────────
export function Billing() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const wantedSku = params.get("plan");
  const [sub, setSub] = useState(null);
  const [mode, setMode] = useState("mock");
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [cardFor, setCardFor] = useState(null); // sku awaiting in-page card entry (sandbox/live)

  const reload = async () => {
    const [s, m, inv] = await Promise.all([
      api.get("/billing/subscription").then((r) => r.data),
      api.get("/payments/mode").then((r) => r.data),
      api.get("/billing/invoices").then((r) => r.data).catch(() => []),
    ]);
    setSub(s); setMode(m.mode); setInvoices(inv);
  };
  useEffect(() => { reload(); }, []);

  const subscribe = async (sku, cardNonce = null) => {
    // Sandbox/live: collect the card in-page first (Square Web Payments SDK), then confirm.
    if (mode !== "mock" && !cardNonce) { setMsg(""); setCardFor(sku); return; }
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/payments/checkout", { sku, origin_url: window.location.origin, card_nonce: cardNonce || undefined });
      if (r.data?.mode === "mock") {
        await refresh();
        await reload();
        setMsg(`Subscribed: ${SKU_LABEL[sku]} (mock mode)`);
      } else if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        setCardFor(null);
        await refresh();
        await reload();
        setMsg(`Welcome to ${SKU_LABEL[sku]} — your subscription is active.`);
      }
    } catch (e) {
      setMsg(getErrorMessage(e) || "Checkout failed");
      if (e?.response?.status === 424) setCardFor(null);
    } finally { setBusy(false); }
  };
  const changePlan = async (sku) => {
    if (!window.confirm(`Switch to ${SKU_LABEL[sku]}?`)) return;
    setBusy(true); setMsg("");
    try { await api.post("/billing/change-plan", { sku }); await refresh(); await reload(); setMsg(`Plan changed to ${SKU_LABEL[sku]}`); }
    catch (e) { setMsg(getErrorMessage(e) || "Change failed"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current period?")) return;
    setBusy(true); setMsg("");
    try { await api.post("/billing/cancel", { at_period_end: true }); await refresh(); await reload(); setMsg("Subscription scheduled to cancel at period end."); }
    catch (e) { setMsg(getErrorMessage(e) || "Cancel failed"); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true); setMsg("");
    try { await api.post("/billing/resume"); await refresh(); await reload(); setMsg("Subscription resumed."); }
    catch (e) { setMsg(getErrorMessage(e) || "Resume failed"); }
    finally { setBusy(false); }
  };

  const active = sub && sub.subscription_id && sub.status === "active";
  const userPlan = user?.plan;
  const planCurrentSku = (sku) => {
    if (active) return sub.sku === sku;
    // No subscription record but user.plan matches (e.g. seeded users) — highlight monthly variant
    if (userPlan === "sense_plus" && sku === "sense_plus_monthly") return true;
    if (userPlan === "luxe"       && sku === "luxe_monthly")       return true;
    return false;
  };
  const SKUs = ["sense_plus_monthly", "sense_plus_annual", "luxe_monthly", "luxe_annual"];
  const fmt$ = (cents, ccy) => cents == null ? "" : `${ccy || "USD"} ${(cents / 100).toFixed(2)}`;
  const isAdmin = user?.role === "admin";

  return (
    <div data-testid="billing-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Billing</p>
      <h1 className="font-heading text-4xl sm:text-5xl text-cocoa">Your subscription.</h1>

      {/* Plan + usage meter */}
      <div className="mt-6">
        <UsageMeter />
      </div>

      {cardFor && <SquareCardStep skuLabel={SKU_LABEL[cardFor]} busy={busy} onCancel={() => setCardFor(null)} onToken={(t) => subscribe(cardFor, t)} />}
      {msg && <div data-testid="billing-msg" className="mt-4 ss-card p-3 text-sm text-cocoa bg-sage/15">{msg}</div>}
      {mode === "mock" && isAdmin && <p className="mt-3 text-xs text-cocoa/50">Square is in <strong>mock</strong> mode — admin view only. Configure in <Link to="/admin-ss" className="underline">Admin → Payments</Link>.</p>}

      {/* Current plan card */}
      <div className="mt-6 ss-card p-6" data-testid="billing-current">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-cocoa/50">Current plan</p>
            <p className="font-heading text-3xl text-cocoa mt-1">
              {active ? SKU_LABEL[sub.sku] : (PLAN_LABEL[user?.plan] || "Free")}
            </p>
            {active && sub.premium_until && (
              <p className="mt-1 text-sm text-cocoa/60">
                Renews / expires {new Date(sub.premium_until).toLocaleDateString()}
                {sub.cancel_at_period_end && <span className="text-rose"> · scheduled to cancel at period end</span>}
              </p>
            )}
          </div>
          {active && (
            <div className="flex gap-2 flex-wrap">
              {sub.cancel_at_period_end ? (
                <button onClick={resume} disabled={busy} data-testid="billing-resume" className="ss-pill ss-pill-primary h-10 disabled:opacity-50">
                  <RefreshCw size={14} /> Resume
                </button>
              ) : (
                <button onClick={cancel} disabled={busy} data-testid="billing-cancel" className="ss-pill ss-pill-ghost h-10 text-rose disabled:opacity-50">
                  <X size={14} /> Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Choose / change plan — driven by DB plan registry */}
      <PlanComparison
        userPlan={user?.plan}
        active={active}
        currentSku={sub?.sku}
        wantedSku={wantedSku}
        onLegacySubscribe={subscribe}
        onLegacyChange={changePlan}
        busy={busy}
      />

      {/* Invoices */}
      <div className="mt-6 ss-card p-6">
        <h2 className="font-heading text-2xl text-cocoa">Invoices</h2>
        {invoices.length === 0 ? (
          <p className="mt-2 text-sm text-cocoa/60">No invoices yet.</p>
        ) : (
          <table className="mt-4 w-full text-sm ss-table-stack" data-testid="billing-invoices">
            <thead><tr className="text-left text-cocoa/50 text-xs uppercase tracking-widest">
              <th className="py-2">Number</th><th>Plan</th><th>Amount</th><th>Status</th><th>Date</th>
            </tr></thead>
            <tbody>
              {invoices.map((iv) => (
                <tr key={iv.id} className="border-t border-champagne" data-testid={`invoice-${iv.id}`}>
                  <td className="py-2 font-mono text-xs">{iv.number}</td>
                  <td data-label="Plan">{SKU_LABEL[iv.sku] || iv.sku || "—"}</td>
                  <td data-label="Amount">{fmt$(iv.amount_cents, iv.currency)}</td>
                  <td data-label="Status"><Pill color={iv.status === "completed" ? "sage" : "rose"}>{iv.status}</Pill></td>
                  <td data-label="Date" className="text-cocoa/60">{iv.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN — tabbed control room ──────────────────────────────────────────

function PlanComparison({ userPlan, active, currentSku, wantedSku, onLegacySubscribe, onLegacyChange, busy }) {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState(wantedSku?.endsWith("_annual") ? "annual" : "monthly");
  const [resolvedPlan, setResolvedPlan] = useState(userPlan || null);
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data?.plans || [])).catch(() => setPlans([]));
    // Resolve the effective plan (admins / beta / luxe users have user.plan=null
    // but resolve to luxe server-side). Hit /usage/panel which uses the same logic.
    api.get("/usage/panel").then((r) => setResolvedPlan(r.data?.plan?.key || userPlan || "free")).catch(() => {});
  }, [userPlan]);
  const LEGACY_SKU = {
    "plus|monthly": "sense_plus_monthly",
    "plus|annual":  "sense_plus_annual",
    "luxe|monthly": "luxe_monthly",
    "luxe|annual":  "luxe_annual",
  };
  const isOnPlan = (key) => (resolvedPlan || "free") === key || (currentSku && currentSku.startsWith(key + "_"));
  return (
    <div className="mt-6 ss-card p-6" data-testid="plan-comparison">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl text-cocoa">{active ? "Change plan" : "Choose a plan"}</h2>
          <p className="mt-1 text-sm text-cocoa/60">Switches take effect immediately; cancellations apply at period end. <Link to="/fair-usage" className="underline hover:text-cocoa">How capacity works →</Link></p>
        </div>
        <div className="inline-flex rounded-full bg-champagne/60 p-1">
          {[["monthly", "Monthly"], ["annual", "Annual (save ~20%)"]].map(([k, l]) => (
            <button key={k} onClick={() => setCycle(k)} data-testid={`billing-cycle-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${cycle === k ? "bg-cocoa text-ivory" : "text-cocoa/65 hover:text-cocoa"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {plans.map((p) => {
          const price = cycle === "annual" ? p.price_annual_usd : p.price_monthly_usd;
          const cadence = cycle === "annual" ? "/yr" : "/mo";
          const sku = LEGACY_SKU[`${p.key}|${cycle}`];
          const onThis = isOnPlan(p.key);
          const wanted = !onThis && wantedSku && sku === wantedSku;
          return (
            <div key={p.key} data-testid={`plan-card-${p.key}`}
              className={`ss-card p-5 flex flex-col gap-3 ${onThis ? "ring-2 ring-mauve" : wanted ? "ring-2 ring-gold" : ""} ${p.highlight ? "border-mauve" : ""}`}>
              <div>
                <div className="flex items-center justify-between">
                  <p className="font-heading text-lg text-cocoa">{p.name}</p>
                  {p.voice_notes_enabled && !onThis && <span data-testid={`plan-voice-badge-${p.key}`} className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-gold border border-gold/50 px-2 py-0.5 rounded-full"><Mic size={9} /> New · Voice</span>}
                  {p.highlight && <span className="text-[10px] tracking-wider uppercase text-mauve bg-mauve/15 px-2 py-0.5 rounded-full">Most chosen</span>}
                  {onThis && <span className="text-[10px] tracking-wider uppercase text-sage bg-sage/15 px-2 py-0.5 rounded-full">Current</span>}
                </div>
                <p className="text-[12px] text-cocoa/60 mt-0.5">{p.tagline}</p>
                <p className="mt-3"><span className="font-heading text-2xl text-cocoa">${price.toFixed(0)}</span><span className="text-[12px] text-cocoa/55 ml-1">{cadence} USD</span></p>
              </div>
              <ul className="space-y-1 text-[13px] text-cocoa/80 flex-1">
                {(p.features || []).slice(0, 5).map((f, i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={12} className="text-sage mt-1 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
              <div>
                {p.key === "free" ? (
                  <span className="ss-pill ss-pill-ghost h-9 w-full justify-center text-xs">Default plan</span>
                ) : onThis ? (
                  <span className="ss-pill ss-pill-ghost h-9 w-full justify-center text-xs" data-testid={`plan-cta-${p.key}-${cycle}`}>
                    <Check size={12} /> Current plan
                  </span>
                ) : sku ? (
                  active ? (
                    <button onClick={() => onLegacyChange(sku)} disabled={busy}
                      data-testid={`plan-cta-${p.key}-${cycle}`}
                      className="ss-pill ss-pill-primary h-9 w-full justify-center text-xs disabled:opacity-40">
                      Switch to {p.name}
                    </button>
                  ) : (
                    <button onClick={() => onLegacySubscribe(sku)} disabled={busy}
                      data-testid={`plan-cta-${p.key}-${cycle}`}
                      className="ss-pill ss-pill-gold h-9 w-full justify-center text-xs disabled:opacity-40">
                      Choose {p.name}
                    </button>
                  )
                ) : (
                  <Link to={`/contact?topic=${encodeURIComponent('Upgrade interest: ' + p.name)}`}
                    data-testid={`plan-cta-${p.key}-${cycle}`}
                    className="ss-pill ss-pill-ghost h-9 w-full justify-center text-xs">
                    Notify me when {p.name} opens
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export function AdminAmbassadorInvites() {
  const [invites, setInvites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: "", invitee_name: "", invitee_email: "",
    expiry_hours: 48, evaluation_days: 180, lifetime_discount_percent: 20,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [lastToken, setLastToken] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/ambassador-invites");
      setInvites(r.data.items || []);
    } catch (e) { setMsg(getErrorMessage(e) || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/ambassador-invites", {
        label: form.label.trim() || null,
        invitee_name: form.invitee_name.trim() || null,
        invitee_email: form.invitee_email.trim() || null,
        expiry_hours: Math.max(1, Math.min(336, Number(form.expiry_hours) || 48)),
        evaluation_days: Math.max(7, Math.min(730, Number(form.evaluation_days) || 180)),
        lifetime_discount_percent: Math.max(0, Math.min(100, Number(form.lifetime_discount_percent) || 20)),
      });
      const token = r.data.token;
      setLastToken({ token, invite_id: r.data.invite.id });
      setMsg("Invite created. Copy the link before closing this dialog — the token is only shown once.");
      setForm({ label: "", invitee_name: "", invitee_email: "",
                expiry_hours: 48, evaluation_days: 180, lifetime_discount_percent: 20 });
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Create failed"); }
    finally { setBusy(false); }
  };
  const revoke = async (inviteId) => {
    if (!window.confirm("Revoke this invite? It cannot be re-activated.")) return;
    try { await api.delete(`/admin/ambassador-invites/${encodeURIComponent(inviteId)}`); await load(); }
    catch (e) { setMsg(getErrorMessage(e) || "Revoke failed"); }
  };
  const resend = async (inviteId) => {
    if (!window.confirm("Revoke this invite and mint a fresh token with the same configuration?")) return;
    try {
      const r = await api.post(`/admin/ambassador-invites/${encodeURIComponent(inviteId)}/resend`);
      setLastToken({ token: r.data.token, invite_id: r.data.invite.id });
      setMsg("Fresh invite minted — old token is now revoked.");
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Resend failed"); }
  };
  const copy = async (token) => {
    try { await navigator.clipboard.writeText(shareUrl(token)); setMsg("Copied invite URL to clipboard."); setTimeout(() => setMsg(""), 2500); }
    catch { setMsg("Couldn't copy — select the URL manually."); }
  };

  return (
    <section data-testid="admin-ambassador-invites" className="mb-8 ss-card p-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-xl text-cocoa">Ambassador invitations</h3>
          <p className="text-cocoa/60 text-xs">Generate a secure, single-use invite link for a trusted early-access user. 256-bit tokens · hashed at rest · expirable · revocable.</p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="ambassador-generate-btn"
          className="ss-pill ss-pill-primary h-10 inline-flex items-center gap-1.5"
        >
          <Plus size={12} /> Generate Ambassador Invite
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} data-testid="ambassador-create-form" className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-ivory border border-champagne rounded-md p-4">
          <Field label="Invite label (internal)">
            <input data-testid="ambassador-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Sarah's friend" maxLength={80}
              className="w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          </Field>
          <Field label="Invitee name (optional)">
            <input data-testid="ambassador-invitee-name" value={form.invitee_name} onChange={(e) => setForm({ ...form, invitee_name: e.target.value })} maxLength={120}
              className="w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          </Field>
          <Field label="Invitee email (optional)">
            <input data-testid="ambassador-invitee-email" type="email" value={form.invitee_email} onChange={(e) => setForm({ ...form, invitee_email: e.target.value })}
              className="w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          </Field>
          <Field label="Access level (locked)">
            <input value="Luxe" disabled className="w-full bg-champagne/40 border border-champagne rounded-md h-10 px-3 text-sm text-cocoa/60" />
          </Field>
          <Field label="Expiry (hours)">
            <input data-testid="ambassador-expiry" type="number" min="1" max="336" value={form.expiry_hours} onChange={(e) => setForm({ ...form, expiry_hours: e.target.value })}
              className="w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          </Field>
          <Field label="Evaluation period (days)">
            <input data-testid="ambassador-eval-days" type="number" min="7" max="730" value={form.evaluation_days} onChange={(e) => setForm({ ...form, evaluation_days: e.target.value })}
              className="w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          </Field>
          <Field label="Lifetime discount % (after evaluation)">
            <input data-testid="ambassador-discount" type="number" min="0" max="100" value={form.lifetime_discount_percent} onChange={(e) => setForm({ ...form, lifetime_discount_percent: e.target.value })}
              className="w-full bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          </Field>
          <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => setShowForm(false)} className="ss-pill ss-pill-ghost h-9 text-[12px]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="ambassador-create-btn" className="ss-pill ss-pill-primary h-9 text-[12px] disabled:opacity-50">{busy ? "Creating…" : "Generate invite"}</button>
          </div>
        </form>
      )}

      {lastToken && (
        <div data-testid="ambassador-new-link" className="mt-4 bg-mauve/5 border border-mauve/30 rounded-md p-3">
          <p className="text-[11px] tracking-[0.3em] uppercase text-mauve">Single-use invite link</p>
          <p className="text-cocoa text-[13px] mt-1 break-all font-mono">{shareUrl(lastToken.token)}</p>
          <div className="mt-2 flex gap-2">
            <button onClick={() => copy(lastToken.token)} className="ss-pill ss-pill-primary h-8 text-[12px]">Copy</button>
            <button onClick={() => setLastToken(null)} className="ss-pill ss-pill-ghost h-8 text-[12px]">Close</button>
          </div>
        </div>
      )}

      {msg && <div data-testid="ambassador-msg" className="mt-3 text-xs text-cocoa/75 bg-sage/15 ss-card p-2 break-all">{msg}</div>}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm ss-table-stack">
          <thead className="bg-champagne/40 text-[10px] uppercase tracking-widest text-cocoa/60">
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Invitee</th>
              <th className="text-center px-3 py-2">Created</th>
              <th className="text-center px-3 py-2">Expires</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-center px-3 py-2">Clicks</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {invites.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-cocoa/50 text-center text-xs">No invites yet — generate one above.</td></tr>}
            {invites.map((i) => {
              const status = i.effective_status || i.status;
              return (
                <tr key={i.id} data-testid={`ambassador-row-${i.id}`} className="border-t border-champagne/50">
                  <td className="px-3 py-2 text-cocoa font-medium sm:font-normal">{i.label || <span className="text-cocoa/40 italic">unnamed</span>}</td>
                  <td data-label="Invitee" className="px-3 py-2 text-cocoa/75 text-xs">
                    {i.invitee_name || "—"}<br className="hidden sm:inline" />
                    <span className="text-cocoa/45 ml-1 sm:ml-0">{i.invitee_email || ""}</span>
                  </td>
                  <td data-label="Created" className="px-3 py-2 text-center text-cocoa/65 text-xs">{(i.created_at || "").slice(0, 10)}</td>
                  <td data-label="Expires" className="px-3 py-2 text-center text-cocoa/65 text-xs">{(i.expires_at || "").slice(0, 10)}</td>
                  <td data-label="Status" className="px-3 py-2 text-center text-xs">
                    {status === "registered" && <span className="text-sage-dark">Registered</span>}
                    {status === "revoked" && <span className="text-rose">Revoked</span>}
                    {status === "expired" && <span className="text-cocoa/45">Expired</span>}
                    {status === "clicked" && <span className="text-mauve">Clicked</span>}
                    {status === "unused" && <span className="text-cocoa/65">Unused</span>}
                  </td>
                  <td data-label="Clicks" className="px-3 py-2 text-center text-cocoa/65 text-xs">{i.click_count || 0}</td>
                  <td data-label="" className="px-3 py-2 text-right whitespace-nowrap">
                    {!i.registered_user_id && !i.revoked_at && (
                      <>
                        <button data-testid={`ambassador-resend-${i.id}`} onClick={() => resend(i.id)} className="ss-pill ss-pill-ghost h-9 px-3 text-xs mr-2">Resend</button>
                        <button data-testid={`ambassador-revoke-${i.id}`} onClick={() => revoke(i.id)} className="ss-pill ss-pill-ghost h-9 px-3 text-xs text-rose">Revoke</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function shareUrl(token) {
  const base = (typeof window !== "undefined") ? window.location.origin : "";
  return `${base}/register?ambassador=${encodeURIComponent(token)}`;
}


export function AdminLite() {
  return <Navigate to="/admin-ss/overview" replace />;
}

export function AdminEssays() {
  const [list, setList] = useState([]);
  const [log, setLog] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [archetypes, setArchetypes] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [style, setStyle] = useState(null);
  const [polishing, setPolishing] = useState(false);
  const [gen, setGen] = useState({ topic: "", category: "patterns", angle: "", scenario: "", auto_publish: false });
  const [batch, setBatch] = useState({ topic: "", category: "patterns", angle: "", scenario: "", archetype_key: "" });
  const [batchProgress, setBatchProgress] = useState(null);
  const [editing, setEditing] = useState(null);
  // Filtering + pagination
  const [filterStatus, setFilterStatus] = useState("all");   // all | published | draft
  const [filterCategory, setFilterCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const load = async () => {
    const [a, b, g, arch, st] = await Promise.all([
      api.get("/admin/essays").then((r) => r.data),
      api.get("/admin/essay-factcheck-log?limit=50").then((r) => r.data).catch(() => []),
      api.get("/admin/essays/topic-groups").then((r) => r.data?.groups || []).catch(() => []),
      api.get("/archetypes").then((r) => r.data || []).catch(() => []),
      api.get("/admin/essays-style-report").then((r) => r.data).catch(() => null),
    ]);
    setList(a || []); setLog(b || []); setTopicGroups(g || []); setArchetypes(arch || []); setStyle(st);
    setPolishing(st?.job?.status === "running");
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!polishing) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [polishing]);
  const styleOf = (slug) => style?.essays?.find((s) => s.slug === slug);
  const polishOne = async (slug) => {
    setBusy(true); setMsg(`Polishing ${slug}…`);
    try {
      const r = await api.post(`/admin/essays/${slug}/polish`, {}, { timeout: 300000 });
      setMsg(`${slug}: ${r.data.status}${r.data.changed ? " (rewritten)" : ""} · before: ${r.data.before} · after: ${r.data.after}`);
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Polish failed"); }
    finally { setBusy(false); }
  };
  const polishAll = async () => {
    const dirty = (style?.total || 0) - (style?.clean || 0);
    if (!window.confirm(`Re-edit ${dirty} essay${dirty === 1 ? "" : "s"} that still fail the house-style check? Runs in the background.`)) return;
    try {
      const r = await api.post("/admin/essays/polish-all");
      setMsg(r.data.started ? `Polishing ${r.data.job.total} essays in the background…` : `Not started: ${r.data.reason}`);
      setPolishing(!!r.data.started);
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Polish all failed"); }
  };
  const generate = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/essays/generate", gen);
      setMsg(`Generated "${r.data.title}" — review and fact-check before publishing.`);
      setGen({ topic: "", category: "patterns", angle: "", scenario: "", auto_publish: false });
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Generation failed"); }
    finally { setBusy(false); }
  };
  const batchGenerate = async (e) => {
    e.preventDefault();
    if (!batch.topic) return;
    setBusy(true); setMsg("");
    setBatchProgress({ status: "running", topic: batch.topic });
    try {
      const r = await api.post("/admin/essays/batch-generate", {
        topic: batch.topic,
        category: batch.category,
        angle: batch.angle || undefined,
        scenario: batch.scenario || undefined,
        archetype_key: batch.archetype_key || undefined,
        auto_publish: true,
      }, { timeout: 600000 });
      const okN = (r.data.variants_created || []).length;
      const errN = (r.data.errors || []).length;
      setMsg(`Batch complete — ${okN}/3 variants created${errN ? ` · ${errN} failed` : ""}. Topic group: ${r.data.topic_group}`);
      setBatchProgress({ status: "done", ...r.data });
      setBatch({ topic: "", category: "patterns", angle: "", scenario: "", archetype_key: "" });
      await load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Batch generation failed");
      setBatchProgress({ status: "error", error: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };
  const factcheckOne = async (slug) => {
    setBusy(true); setMsg(`Fact-checking ${slug}…`);
    try {
      const r = await api.post(`/admin/essays/${slug}/factcheck`);
      setMsg(`${slug}: ${r.data.status}${r.data.issues ? ` (${r.data.issues} issues)` : ""} — ${r.data.summary || ""}`);
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Fact-check failed"); }
    finally { setBusy(false); }
  };
  const factcheckAll = async () => {
    if (!window.confirm("Run AI fact-check across ALL essays? Takes ~1–3 minutes.")) return;
    setBusy(true); setMsg("Fact-checking all essays…");
    try {
      const r = await api.post("/admin/essays/factcheck-all", {}, { timeout: 600000 });
      const c = r.data?.counts || {};
      setMsg(`Done — ${r.data?.total} essays · ${Object.entries(c).map(([k, v]) => `${k}: ${v}`).join(" · ")}`);
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Fact-check all failed"); }
    finally { setBusy(false); }
  };
  const openEdit = async (slug) => {
    const r = await api.get(`/admin/essays/${slug}`);
    setEditing(r.data);
  };
  const save = async () => {
    if (!editing) return;
    setBusy(true); setMsg("");
    try {
      await api.put(`/admin/essays/${editing.slug}`, {
        title: editing.title, subtitle: editing.subtitle, category: editing.category,
        body_md: editing.body_md, published: editing.published, fact_checked: editing.fact_checked,
      });
      setMsg(`Saved "${editing.title}".`); setEditing(null); await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Delete this essay permanently?")) return;
    await api.delete(`/admin/essays/${slug}`);
    await load();
  };
  const togglePublished = async (essay) => {
    const next = !essay.published;
    await api.put(`/admin/essays/${essay.slug}`, { published: next });
    setMsg(next ? `Showing "${essay.title}" on the editorial page.` : `Hidden "${essay.title}" from the editorial page.`);
    await load();
  };
  return (
    <div className="space-y-5" data-testid="admin-essays">
      {msg && <div className="ss-card p-3 text-sm bg-sage/15 text-cocoa">{msg}</div>}

      {/* Generation form */}
      <form onSubmit={generate} className="ss-card-premium p-5 space-y-3">
        <h3 className="font-heading text-xl text-cocoa">Generate a new essay (AI)</h3>
        <p className="text-xs text-cocoa/60">Uses gpt-5.1 via Emergent LLM Key. Always fact-check before publishing.</p>
        <input data-testid="essay-gen-topic" placeholder="Topic (e.g., 'Reading provider behaviour')" maxLength={200}
          value={gen.topic} onChange={(e) => setGen({...gen, topic: e.target.value})} required
          className="w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
        <select data-testid="essay-gen-category" value={gen.category} onChange={(e) => setGen({...gen, category: e.target.value})}
          className="w-full bg-ivory border border-champagne rounded-md h-11 px-3">
          <option value="patterns">Patterns</option>
          <option value="provider_psychology">Provider psychology</option>
          <option value="mixed_signals">Mixed signals</option>
          <option value="emotional_intelligence">Emotional intelligence</option>
          <option value="archetype_explainer">Archetype explainer</option>
          <option value="lifestyle">Lifestyle</option>
        </select>
        <textarea data-testid="essay-gen-angle" placeholder="Angle / what to emphasise (optional)" rows={3} maxLength={500}
          value={gen.angle} onChange={(e) => setGen({...gen, angle: e.target.value})}
          className="w-full bg-ivory border border-champagne rounded-md p-3" />
        <textarea data-testid="essay-gen-scenario" placeholder="Scenario for the opening story (optional — e.g., 'Anya, 28, sits at a hotel bar waiting; her partner Lukas, 52, has cancelled their trip for the third time. He texts that he will make it up to her with Capri next month.')" rows={4} maxLength={1200}
          value={gen.scenario} onChange={(e) => setGen({...gen, scenario: e.target.value})}
          className="w-full bg-ivory border border-champagne rounded-md p-3" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={gen.auto_publish} onChange={(e) => setGen({...gen, auto_publish: e.target.checked})} />
          Auto-publish (skip review)
        </label>
        <button type="submit" disabled={busy || !gen.topic} data-testid="essay-gen-submit" className="ss-pill ss-pill-primary h-11 disabled:opacity-50">
          {busy ? "Writing…" : <><Sparkles size={14} /> Generate</>}
        </button>
      </form>

      {/* Batch identity-variant generator */}
      <form onSubmit={batchGenerate} className="ss-card-premium p-5 space-y-3" data-testid="admin-essay-batch-generate">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading text-xl text-cocoa">Generate inclusive variants for one topic</h3>
            <p className="text-xs text-cocoa/60 mt-1">
              Produces 3 identity-tailored variants in one call: <strong>universal</strong> (they/them),
              <strong> partner_female</strong> (she/her partner), and <strong>enm_poly</strong> (multi-partner).
              Readers are matched to the best variant from their profile signals. Takes ~2–3 minutes.
            </p>
          </div>
          <span className="ss-pill ss-pill-ghost h-7 text-xs">{topicGroups.length} topic group{topicGroups.length === 1 ? "" : "s"}</span>
        </div>
        <input data-testid="essay-batch-topic" placeholder="Topic (e.g., 'When silence between texts gets louder than words')" maxLength={200}
          value={batch.topic} onChange={(e) => setBatch({...batch, topic: e.target.value})} required
          className="w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select data-testid="essay-batch-category" value={batch.category} onChange={(e) => setBatch({...batch, category: e.target.value})}
            className="w-full bg-ivory border border-champagne rounded-md h-11 px-3">
            <option value="patterns">Patterns</option>
            <option value="provider_psychology">Provider psychology</option>
            <option value="mixed_signals">Mixed signals</option>
            <option value="emotional_intelligence">Emotional intelligence</option>
            <option value="archetype_explainer">Archetype explainer</option>
            <option value="lifestyle">Lifestyle</option>
          </select>
          <select data-testid="essay-batch-archetype" value={batch.archetype_key} onChange={(e) => setBatch({...batch, archetype_key: e.target.value})}
            className="w-full bg-ivory border border-champagne rounded-md h-11 px-3">
            <option value="">Archetype (optional)</option>
            {archetypes.map((a) => (
              <option key={a.key} value={a.key}>{a.name}</option>
            ))}
          </select>
        </div>
        <textarea data-testid="essay-batch-angle" placeholder="Angle / what to emphasise (optional — applied to ALL variants)" rows={2} maxLength={500}
          value={batch.angle} onChange={(e) => setBatch({...batch, angle: e.target.value})}
          className="w-full bg-ivory border border-champagne rounded-md p-3" />
        <textarea data-testid="essay-batch-scenario" placeholder="Scenario seed for the opening vignette (optional — invented detail will be tailored per variant)" rows={3} maxLength={1200}
          value={batch.scenario} onChange={(e) => setBatch({...batch, scenario: e.target.value})}
          className="w-full bg-ivory border border-champagne rounded-md p-3" />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs text-cocoa/50">All 3 variants auto-publish. Fact-check before promoting widely.</span>
          <button type="submit" disabled={busy || !batch.topic} data-testid="essay-batch-submit" className="ss-pill ss-pill-primary h-11 disabled:opacity-50">
            {busy ? "Generating 3 variants…" : <><Sparkles size={14} /> Generate identity matrix</>}
          </button>
        </div>
        {batchProgress?.status === "done" && (
          <div className="ss-card p-3 text-sm bg-sage/15 text-cocoa space-y-1" data-testid="essay-batch-result">
            <div className="font-medium">Topic group: <span className="font-mono text-xs">{batchProgress.topic_group}</span></div>
            <ul className="text-xs space-y-0.5">
              {(batchProgress.variants_created || []).map((v) => (
                <li key={v.slug}>✓ <span className="font-medium">{v.variant}</span> — <Link to={`/essays/${v.slug}`} target="_blank" rel="noopener noreferrer" className="underline">{v.title}</Link></li>
              ))}
              {(batchProgress.errors || []).map((e) => (
                <li key={e.variant} className="text-rose-700">✗ <span className="font-medium">{e.variant}</span> — {e.error}</li>
              ))}
            </ul>
          </div>
        )}
      </form>

      {/* Existing topic-groups overview */}
      {topicGroups.length > 0 && (
        <div className="ss-card p-5" data-testid="admin-essay-topic-groups">
          <h3 className="font-heading text-xl text-cocoa mb-3">Identity variant coverage</h3>
          <div className="space-y-2">
            {topicGroups.map((g) => (
              <div key={g.topic_group} className="flex items-start justify-between gap-3 py-2 border-b border-champagne/40 last:border-0">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-cocoa/70 truncate">{g.topic_group}</div>
                  <div className="text-xs text-cocoa/60 mt-0.5">
                    {g.variants.map((v) => v.variant_label).join(" · ")}
                  </div>
                </div>
                <span className={`ss-pill h-7 text-xs ${g.variant_count >= 3 ? "ss-pill-primary" : "ss-pill-ghost"}`}>
                  {g.variant_count}/3
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Essays list — filters + pagination */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-cocoa/70">{list.length} total · {list.filter((e) => e.fact_checked).length} fact-checked · {list.filter((e) => e.published).length} published · {list.filter((e) => !e.published).length} hidden</p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={polishAll} disabled={busy || polishing || !style || style.clean === style.total} data-testid="essay-polish-all" className="ss-pill ss-pill-ghost h-10 disabled:opacity-50">
            <Sparkles size={14} /> {polishing ? `Polishing ${style?.job?.done ?? 0}/${style?.job?.total ?? "…"}` : "Polish all"}
          </button>
          <button onClick={factcheckAll} disabled={busy} data-testid="essay-factcheck-all" className="ss-pill ss-pill-primary h-10 disabled:opacity-50">
            <ShieldCheck size={14} /> Fact-check all
          </button>
        </div>
      </div>
      {style && (
        <p className="text-xs text-cocoa/60" data-testid="essay-style-summary">
          House style: {style.clean}/{style.total} essays read clean (no dashes, no sentence-opening But/And/So, no stock AI phrasing).
          {style.total - style.clean > 0 && ` ${style.total - style.clean} still need a polish.`}
        </p>
      )}

      <div className="ss-card p-3 flex flex-wrap items-center gap-2">
        <input data-testid="admin-essay-search" placeholder="Search title / category" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="flex-1 min-w-[180px] bg-ivory border border-champagne rounded-md h-9 px-3 text-sm" />
        <select data-testid="admin-essay-filter-status" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
          className="bg-ivory border border-champagne rounded-md h-9 px-2 text-sm">
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Hidden / draft</option>
        </select>
        <select data-testid="admin-essay-filter-category" value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setPage(0); }}
          className="bg-ivory border border-champagne rounded-md h-9 px-2 text-sm">
          <option value="all">All categories</option>
          <option value="patterns">Patterns</option>
          <option value="provider_psychology">Provider psychology</option>
          <option value="mixed_signals">Mixed signals</option>
          <option value="emotional_intelligence">Emotional intelligence</option>
          <option value="archetype_explainer">Archetype explainer</option>
          <option value="lifestyle">Lifestyle</option>
        </select>
      </div>

      {(() => {
        const q = search.trim().toLowerCase();
        const filtered = list.filter((e) => {
          if (filterStatus === "published" && !e.published) return false;
          if (filterStatus === "draft" && e.published) return false;
          if (filterCategory !== "all" && e.category !== filterCategory) return false;
          if (q && !`${e.title} ${e.category} ${e.slug}`.toLowerCase().includes(q)) return false;
          return true;
        });
        const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const safePage = Math.min(page, pages - 1);
        const slice = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
        return (
          <>
            <div className="space-y-3">
              {filtered.length === 0 && <p className="text-sm text-cocoa/60">No essays match the current filters.</p>}
              {slice.map((e) => (
                <div key={e.slug} data-testid={`admin-essay-${e.slug}`} className="ss-card p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading text-xl text-cocoa truncate">{e.title}</p>
                        <Pill color={e.published ? "sage" : "rose"}>{e.published ? "published" : "hidden"}</Pill>
                        {e.fact_checked && <Pill color="sage">fact-checked</Pill>}
                        {styleOf(e.slug) && (styleOf(e.slug).clean
                          ? <Pill color="sage">reads human</Pill>
                          : <Pill color="rose">style: {styleOf(e.slug).summary}</Pill>)}
                        {e.ai_generated && <Pill>AI-written</Pill>}
                      </div>
                      <p className="mt-1 text-xs text-cocoa/60">/{e.slug} · {e.category} · {e.reading_time_min} min{e.fact_checked_at ? ` · checked ${e.fact_checked_at.slice(0, 10)}` : ""}</p>
                      {e.fact_check_summary && <p className="mt-2 text-xs text-cocoa/70 italic line-clamp-2">"{e.fact_check_summary}"</p>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Link to={`/essays/${e.slug}`} target="_blank" rel="noopener noreferrer" className="ss-pill ss-pill-ghost h-9">View</Link>
                      <button onClick={() => factcheckOne(e.slug)} disabled={busy} data-testid={`admin-essay-${e.slug}-factcheck`} className="ss-pill ss-pill-ghost h-9 disabled:opacity-50"><ShieldCheck size={12} /> Check</button>
                      <button onClick={() => polishOne(e.slug)} disabled={busy || polishing} data-testid={`admin-essay-${e.slug}-polish`} className="ss-pill ss-pill-ghost h-9 disabled:opacity-50"><Sparkles size={12} /> Polish</button>
                      <button onClick={() => togglePublished(e)} data-testid={`admin-essay-${e.slug}-toggle`} className="ss-pill ss-pill-ghost h-9">{e.published ? "Hide" : "Show"}</button>
                      <button onClick={() => openEdit(e.slug)} data-testid={`admin-essay-${e.slug}-edit`} className="ss-pill ss-pill-primary h-9">Edit</button>
                      <button onClick={() => remove(e.slug)} className="ss-pill ss-pill-ghost h-9 text-rose">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm text-cocoa/60" data-testid="admin-essay-pagination">
                <span>Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                <div className="flex gap-2">
                  <button disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40">Prev</button>
                  <span className="px-2 self-center">Page {safePage + 1} / {pages}</span>
                  <button disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Editor */}
      {editing && (
        <div className="ss-card-premium p-6 border-mauve" data-testid="essay-editor">
          <h3 className="font-heading text-2xl text-cocoa">Edit essay</h3>
          <div className="mt-4 space-y-3">
            <input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} maxLength={160}
              className="w-full bg-ivory border border-champagne rounded-md h-11 px-3 text-lg font-heading" />
            <input value={editing.subtitle || ""} onChange={(e) => setEditing({...editing, subtitle: e.target.value})} maxLength={240} placeholder="Subtitle"
              className="w-full bg-ivory border border-champagne rounded-md h-11 px-3 italic" />
            <textarea data-testid="essay-edit-body" value={editing.body_md} onChange={(e) => setEditing({...editing, body_md: e.target.value})}
              rows={22} maxLength={50000}
              className="w-full font-mono text-xs bg-ivory border border-champagne rounded-md p-3" />
            <div className="flex gap-4 flex-wrap items-center">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="essay-edit-published" checked={editing.published} onChange={(e) => setEditing({...editing, published: e.target.checked})} />
                Published
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="essay-edit-factchecked" checked={!!editing.fact_checked} onChange={(e) => setEditing({...editing, fact_checked: e.target.checked})} />
                Fact-checked
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={save} disabled={busy} data-testid="essay-edit-save" className="ss-pill ss-pill-primary h-11 disabled:opacity-50">Save</button>
              <button onClick={() => setEditing(null)} className="ss-pill ss-pill-ghost h-11">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Recent fact-check log */}
      <div className="ss-card p-5 mt-6" data-testid="essay-factcheck-log">
        <h3 className="font-heading text-xl text-cocoa">Recent fact-checks</h3>
        {log.length === 0 ? <p className="mt-2 text-sm text-cocoa/60">No fact-checks yet — click "Fact-check all" above.</p> : (
          <table className="mt-3 w-full text-sm ss-table-stack">
            <thead><tr className="text-left text-cocoa/50 text-xs uppercase tracking-widest">
              <th className="py-2">Essay</th><th>Verdict</th><th>Issues</th><th>Checked</th>
            </tr></thead>
            <tbody>
              {log.slice(0, 20).map((l) => (
                <tr key={l.id} className="border-t border-champagne">
                  <td className="py-2 sm:truncate sm:max-w-[280px] font-medium sm:font-normal">{l.essay_title}</td>
                  <td data-label="Verdict"><Pill color={l.verdict === "pass" ? "sage" : l.verdict === "needs_edit" ? "champagne" : "rose"}>{l.verdict}</Pill></td>
                  <td data-label="Issues">{(l.issues || []).length}</td>
                  <td data-label="Checked" className="text-cocoa/60">{l.checked_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const OVERVIEW_CARDS = [
  ["users", "Members"], ["paid_users", "Paid plans"], ["ambassadors", "Ambassadors"],
  ["advisor_sessions_30d", "Advisor reads · 30d"], ["mentor_sessions", "Mentor sessions"],
  ["relationship_profiles", "Relationship profiles"], ["essays_published", "Essays live"],
  ["embeddings", "Memory vectors"], ["ai_calls_30d", "AI calls · 30d"],
  ["ai_cost_usd_30d", "AI spend · 30d"], ["contact_unread", "Unread contact"],
];

export function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setStats(r.data || {})).catch(() => setStats({}));
  }, []);
  const fmt = (k, v) => (typeof v !== "number" ? "—" : k === "ai_cost_usd_30d" ? `$${v.toFixed(2)}` : v.toLocaleString());
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {OVERVIEW_CARDS.map(([k, label]) => (
          <div key={k} className="ss-card p-4" data-testid={`admin-overview-${k}`}>
            <p className="text-xs uppercase tracking-widest text-cocoa/50">{label}</p>
            <p className="font-heading text-3xl text-cocoa mt-1">{stats ? fmt(k, stats[k]) : "…"}</p>
          </div>
        ))}
      </div>
      {stats?.plans && (
        <div className="ss-card p-4 mt-4" data-testid="admin-overview-plans">
          <p className="text-xs uppercase tracking-widest text-cocoa/50">Members by plan</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(stats.plans).map(([k, v]) => (
              <span key={k} className="px-3 py-1 rounded-full bg-champagne/60 text-cocoa text-sm">{PLAN_LABEL[k] || k} · {v}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSquare() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [pricing, setPricing] = useState({});
  const [msg, setMsg] = useState("");
  const load = async () => {
    const [c, p] = await Promise.all([
      api.get("/admin/square-config").then((r) => r.data),
      api.get("/admin/pricing").then((r) => r.data),
    ]);
    setCfg(c); setForm({ mode: c.mode }); setPricing(p);
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setMsg("");
    try { await api.put("/admin/square-config", form); setMsg("Square config saved."); await load(); }
    catch (e) { setMsg(getErrorMessage(e) || "Save failed"); }
  };
  const savePricing = async () => {
    setMsg("");
    try { await api.put("/admin/pricing", pricing); setMsg("Pricing saved."); await load(); }
    catch (e) { setMsg(getErrorMessage(e) || "Save failed"); }
  };
  if (!cfg) return <p className="text-cocoa/50">Loading…</p>;
  const fields = [
    { group: "Sandbox", items: [
      ["sandbox_application_id", "Application ID"],
      ["sandbox_access_token",   "Access Token"],
      ["sandbox_location_id",    "Location ID"],
      ["sandbox_webhook_signature_key", "Webhook Signature Key"],
      ["sandbox_webhook_notification_url", "Webhook Notification URL"],
      ["sandbox_sense_plus_monthly_plan_id", "Sense+ Monthly Plan Variation ID"],
      ["sandbox_sense_plus_annual_plan_id",  "Sense+ Annual Plan Variation ID"],
      ["sandbox_luxe_monthly_plan_id",       "Luxe Monthly Plan Variation ID"],
      ["sandbox_luxe_annual_plan_id",        "Luxe Annual Plan Variation ID"],
    ]},
    { group: "Production", items: [
      ["prod_application_id", "Application ID"],
      ["prod_access_token",   "Access Token"],
      ["prod_location_id",    "Location ID"],
      ["prod_webhook_signature_key", "Webhook Signature Key"],
      ["prod_webhook_notification_url", "Webhook Notification URL"],
      ["prod_sense_plus_monthly_plan_id", "Sense+ Monthly Plan Variation ID"],
      ["prod_sense_plus_annual_plan_id",  "Sense+ Annual Plan Variation ID"],
      ["prod_luxe_monthly_plan_id",       "Luxe Monthly Plan Variation ID"],
      ["prod_luxe_annual_plan_id",        "Luxe Annual Plan Variation ID"],
    ]},
  ];
  const HELP = {
    application_id: "Developer Console → your app → Credentials. Safe to expose to the browser (used by the card form).",
    access_token: "Developer Console → Credentials. Secret — stored encrypted, never sent to the browser. Needs Subscriptions, Customers, Payments, Invoices and Orders permissions.",
    location_id: "Developer Console → Locations (the seller location subscriptions are billed under).",
    webhook_signature_key: "Developer Console → Webhooks → your subscription → Endpoint details → Signature key. Used to verify every webhook.",
    webhook_notification_url: `Exactly the URL registered in Square's webhook subscription — normally ${window.location.origin}/api/payments/square/webhook. Must match character-for-character or signatures fail.`,
    plan_id: "Square Dashboard → Items → Subscription plans → the plan variation (price + cadence). Sandbox and production IDs are different objects — never mix them.",
  };
  const helpFor = (k) => HELP[k.replace(/^(sandbox|prod)_/, "").replace(/^(sense_plus|luxe)_(monthly|annual)_plan_id$/, "plan_id")];
  return (
    <div className="space-y-5" data-testid="admin-square">
      {msg && <div className="ss-card p-3 text-sm bg-sage/15 text-cocoa">{msg}</div>}
      <div className="ss-card p-4 text-sm text-cocoa/75 leading-relaxed" data-testid="square-readiness">
        <p className="font-heading text-lg text-cocoa">Going live checklist</p>
        <ol className="mt-2 list-decimal pl-5 space-y-1">
          <li>Fill every <strong>Production</strong> field below (keys from your Square Developer Console; plan variation IDs from the Square Dashboard).</li>
          <li>In Square → Webhooks, subscribe the production endpoint to <code>subscription.created</code>, <code>subscription.updated</code>, <code>invoice.payment_made</code>, <code>payment.updated</code> and paste its signature key + exact URL here.</li>
          <li>Switch <strong>Mode</strong> to <em>live</em> and run one low-value real subscription. Until then, members who reach the payment step see a friendly "payments are almost ready" message — nothing is charged.</li>
        </ol>
        <p className="mt-2 text-xs text-cocoa/50">If Square is unreachable at any point, members get "our payment partner didn't respond — your card has not been charged" and can retry; every attempt is audited.</p>
      </div>
      <div className="ss-card p-5">
        <h3 className="font-heading text-xl text-cocoa mb-3">Mode</h3>
        <select data-testid="square-mode" value={form.mode || cfg.mode} onChange={set("mode")}
          className="bg-ivory border border-champagne rounded-md h-11 px-3 w-full max-w-xs">
          {["mock", "sandbox", "live"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <p className="mt-2 text-xs text-cocoa/50">
          Current: <strong>{cfg.mode}</strong> · sandbox configured: {cfg.sandbox_configured ? "yes" : "no"} · prod: {cfg.prod_configured ? "yes" : "no"}
        </p>
      </div>
      {fields.map((group) => (
        <div key={group.group} className="ss-card p-5">
          <h3 className="font-heading text-xl text-cocoa mb-3">{group.group}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {group.items.map(([k, lbl]) => (
              <label key={k} className="block min-w-0">
                <span className="text-xs uppercase tracking-widest text-cocoa/50">{lbl}</span>
                <input data-testid={`square-${k}`} type={k.includes("access_token") || k.includes("signature") ? "password" : "text"}
                  placeholder={cfg[`${k}_masked`] || cfg[k] || ""}
                  onChange={set(k)}
                  className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
                {helpFor(k) && <span className="block mt-1 text-[11px] text-cocoa/50 leading-snug break-words" data-testid={`square-help-${k}`}>{helpFor(k)}</span>}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button onClick={save} data-testid="square-save" className="ss-pill ss-pill-primary h-11">Save Square config</button>

      <div className="ss-card p-5 mt-6">
        <h3 className="font-heading text-xl text-cocoa">Pricing</h3>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {["sense_plus_monthly_usd","sense_plus_annual_usd","luxe_monthly_usd","luxe_annual_usd"].map((k) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-widest text-cocoa/50">{k.replace(/_/g, " ").replace(" usd", " (USD)")}</span>
              <input type="number" min="0" step="0.01" data-testid={`pricing-${k}`}
                value={pricing[k] ?? ""} onChange={(e) => setPricing({...pricing, [k]: parseFloat(e.target.value) || 0})}
                className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3 outline-none focus:border-mauve" />
            </label>
          ))}
        </div>
        <button onClick={savePricing} data-testid="pricing-save" className="mt-4 ss-pill ss-pill-primary h-11">Save pricing</button>
      </div>
    </div>
  );
}

export function AdminEmail() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ sendgrid_api_key: "", sender_email: "", enabled: false });
  const [testTo, setTestTo] = useState("");
  const [msg, setMsg] = useState("");
  const load = async () => {
    const r = await api.get("/admin/email-settings").then((x) => x.data);
    setCfg(r); setForm({ sendgrid_api_key: "", sender_email: r.sender_email || "", enabled: !!r.enabled });
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setMsg("");
    try {
      const payload = { enabled: form.enabled, sender_email: form.sender_email };
      if (form.sendgrid_api_key && !form.sendgrid_api_key.includes("•")) payload.sendgrid_api_key = form.sendgrid_api_key;
      await api.put("/admin/email-settings", payload);
      setMsg("Email settings saved.");
      await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Save failed"); }
  };
  const sendTest = async () => {
    setMsg("");
    try { const r = await api.post("/admin/email-test", { to: testTo }); setMsg(r.data?.sent ? `Test sent to ${testTo}.` : "Not sent — SendGrid is disabled or unconfigured."); }
    catch (e) { setMsg(getErrorMessage(e) || "Send failed"); }
  };
  if (!cfg) return <p className="text-cocoa/50">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="admin-email">
      {msg && <div className="ss-card p-3 text-sm bg-sage/15 text-cocoa">{msg}</div>}
      <div className="ss-card p-5">
        <h3 className="font-heading text-xl text-cocoa">SendGrid</h3>
        <p className="text-xs text-cocoa/60 mt-1">
          Status: {cfg.configured ? <span className="text-mauve">Configured</span> : <span className="text-rose">Not configured</span>} ·
          Enabled: {cfg.enabled ? "yes" : "no"}
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-cocoa/50">API Key</span>
            <input data-testid="email-api-key" type="password" placeholder={cfg.api_key_masked || "SG.•••"}
              value={form.sendgrid_api_key} onChange={(e) => setForm({...form, sendgrid_api_key: e.target.value})}
              className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-cocoa/50">Sender email</span>
            <input data-testid="email-sender" type="email" placeholder="hello@sugarsense.app"
              value={form.sender_email} onChange={(e) => setForm({...form, sender_email: e.target.value})}
              className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
          </label>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input data-testid="email-enabled" type="checkbox" checked={form.enabled} onChange={(e) => setForm({...form, enabled: e.target.checked})} />
          Enable email transport
        </label>
        <div className="mt-5 flex gap-3 flex-wrap items-end">
          <button onClick={save} data-testid="email-save" className="ss-pill ss-pill-primary h-11">Save</button>
          <label className="block flex-1 min-w-[220px]">
            <span className="text-xs uppercase tracking-widest text-cocoa/50">Send test to</span>
            <input data-testid="email-test-to" type="email" placeholder="you@yourdomain.com" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
          </label>
          <button onClick={sendTest} disabled={!testTo} data-testid="email-test-send" className="ss-pill ss-pill-ghost h-11 disabled:opacity-50">Send test</button>
        </div>
        <p className="mt-3 text-xs text-cocoa/50">
          Need a key? Get one at <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-mauve underline">app.sendgrid.com</a>.
          The key is encrypted at rest before storage.
        </p>
      </div>
    </div>
  );
}

export function AdminCampaigns() {
  const [list, setList] = useState([]);
  const [log, setLog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/campaigns").then((r) => r.data),
      api.get("/admin/campaigns/log?limit=50").then((r) => r.data).catch(() => []),
    ]);
    setList(a); setLog(b);
  };
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy(true); setMsg("");
    try { const r = await api.post("/admin/campaigns/run-now"); setMsg(`Tick complete: ${JSON.stringify(r.data)}`); await load(); }
    catch (e) { setMsg(getErrorMessage(e) || "Run failed"); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!editing) return;
    setBusy(true); setMsg("");
    try {
      await api.put(`/admin/campaigns/${editing.key}`, {
        name: editing.name, enabled: editing.enabled, delay_days: editing.delay_days,
        subject: editing.subject, html: editing.html,
      });
      setMsg(`Saved "${editing.name}".`); setEditing(null); await load();
    } catch (e) { setMsg(getErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };
  const sendTest = async (key) => {
    const to = window.prompt("Send test to which email?");
    if (!to) return;
    try { const r = await api.post(`/admin/campaigns/${key}/test`, { to }); setMsg(r.data?.sent ? `Test sent to ${to}.` : "Not sent (SendGrid disabled)."); }
    catch (e) { setMsg(getErrorMessage(e) || "Test failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-campaigns">
      {msg && <div className="ss-card p-3 text-sm bg-sage/15 text-cocoa">{msg}</div>}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-cocoa/70">4 lifecycle campaigns. Edit subject/copy/delay anytime. SendGrid must be enabled.</p>
        <button onClick={runNow} disabled={busy} data-testid="campaign-run-now" className="ss-pill ss-pill-ghost h-10 shrink-0 disabled:opacity-50"><RefreshCw size={14} /> Run scheduler now</button>
      </div>

      <div className="space-y-3">
        {list.map((c) => (
          <div key={c.key} data-testid={`campaign-${c.key}`} className="ss-card p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading text-xl text-cocoa">{c.name}</p>
                  <Pill color={c.enabled ? "sage" : "rose"}>{c.enabled ? "on" : "off"}</Pill>
                  <Pill>day {c.delay_days}</Pill>
                </div>
                <p className="mt-1 text-sm text-cocoa/70 truncate">Subject: <em>{c.subject}</em></p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => sendTest(c.key)} data-testid={`campaign-${c.key}-test`} className="ss-pill ss-pill-ghost h-9">Test</button>
                <button onClick={() => setEditing({...c})} data-testid={`campaign-${c.key}-edit`} className="ss-pill ss-pill-primary h-9">Edit</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="ss-card p-6 mt-4 border-mauve" data-testid="campaign-editor">
          <h3 className="font-heading text-2xl text-cocoa">Edit: {editing.name}</h3>
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-cocoa/50">Name</span>
              <input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} maxLength={100}
                className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-cocoa/50">Subject</span>
              <input data-testid="campaign-edit-subject" value={editing.subject} onChange={(e) => setEditing({...editing, subject: e.target.value})} maxLength={200}
                className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cocoa/50">Delay (days after signup)</span>
                <input type="number" min="0" max="120" data-testid="campaign-edit-delay" value={editing.delay_days} onChange={(e) => setEditing({...editing, delay_days: parseInt(e.target.value) || 0})}
                  className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
              </label>
              <label className="flex items-center gap-2 self-end pb-2">
                <input type="checkbox" data-testid="campaign-edit-enabled" checked={editing.enabled} onChange={(e) => setEditing({...editing, enabled: e.target.checked})} />
                Enabled
              </label>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-cocoa/50">HTML body (supports {`{{first_name}}`} and {`{{app_url}}`})</span>
              <textarea data-testid="campaign-edit-html" value={editing.html} onChange={(e) => setEditing({...editing, html: e.target.value})} rows={10} maxLength={20000}
                className="mt-1 w-full font-mono text-xs bg-ivory border border-champagne rounded-md p-3" />
            </label>
            <div className="flex gap-2">
              <button onClick={save} disabled={busy} data-testid="campaign-edit-save" className="ss-pill ss-pill-primary h-11 disabled:opacity-50">Save campaign</button>
              <button onClick={() => setEditing(null)} className="ss-pill ss-pill-ghost h-11">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="ss-card p-5 mt-6">
        <h3 className="font-heading text-xl text-cocoa">Recent sends</h3>
        {log.length === 0 ? <p className="mt-2 text-sm text-cocoa/60">No sends yet.</p> : (
          <table className="mt-3 w-full text-sm ss-table-stack" data-testid="campaign-log">
            <thead><tr className="text-left text-cocoa/50 text-xs uppercase tracking-widest">
              <th className="py-2">Campaign</th><th>User</th><th>Status</th><th>Sent</th>
            </tr></thead>
            <tbody>
              {log.map((l, i) => (
                <tr key={i} className="border-t border-champagne">
                  <td className="py-2 font-medium sm:font-normal">{l.campaign_key}</td>
                  <td data-label="User" className="font-mono text-xs">{l.user_id?.slice(0, 8)}…</td>
                  <td data-label="Status"><Pill color={l.status === "sent" ? "sage" : "rose"}>{l.status}</Pill></td>
                  <td data-label="Sent" className="text-cocoa/60">{l.sent_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}



// ─── ONBOARDING ────────────────────────────────────────────────────────────
export function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [alias, setAlias] = useState("");
  const [stage, setStage] = useState("");
  const [what, setWhat] = useState("");
  const [busy, setBusy] = useState(false);

  const skipAll = () => nav("/dashboard");

  const createProfileAndAsk = async () => {
    setBusy(true);
    let profileId = null;
    try {
      if (alias.trim()) {
        const r = await api.post("/profiles", { alias: alias.trim(), stage: stage || undefined });
        profileId = r.data?.id;
      }
    } catch {}
    setBusy(false);
    if (what.trim()) {
      try {
        localStorage.setItem("ss_ask_draft_v1", JSON.stringify({
          form: {
            what_happened: what.trim(),
            relationship_stage: stage, user_goal: "", his_recent_behaviour: "",
            promises_made: "", follow_through: "", emotional_state: "", considering_next: "",
          },
          profileId: profileId || "",
          ts: Date.now(),
        }));
      } catch {}
    }
    nav(profileId ? `/ask?profile_id=${profileId}` : "/ask");
  };

  const next = () => {
    if (step === 1 && !alias.trim()) { nav("/ask"); return; }
    setStep(2);
  };

  return (
    <div data-testid="onboarding-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="text-center mb-8">
        <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Step {step} of 2</p>
        <h1 className="font-heading text-3xl sm:text-5xl text-cocoa mt-3 leading-tight">
          {step === 1 ? "Who are you reading?" : "What's happening?"}
        </h1>
        <p className="mt-3 text-cocoa/70 max-w-md mx-auto leading-relaxed">
          {step === 1
            ? "Add an alias for the person you're navigating. Never their real name. You can skip and add this later."
            : "Tell me what's on your mind, the way you'd tell a friend. You'll get your first archetype read in seconds."}
        </p>
      </div>

      {step === 1 ? (
        <div className="ss-card-premium p-6 sm:p-8 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-cocoa/50">Alias</span>
            <input data-testid="onboarding-alias" value={alias} onChange={(e) => setAlias(e.target.value)}
              placeholder="M.  /  J.R.  /  T." maxLength={40}
              className="mt-1 w-full bg-ivory border border-champagne rounded-md h-12 px-4 outline-none focus:border-mauve" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-cocoa/50">Where are things at?</span>
            <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="onboarding-stage"
              className="mt-1 w-full bg-ivory border border-champagne rounded-md h-12 px-3">
              <option value="">— Skip —</option>
              {Object.entries(STAGE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={next} data-testid="onboarding-next" className="ss-pill ss-pill-primary h-12 flex-1">Continue</button>
            <button onClick={skipAll} className="ss-pill ss-pill-ghost h-12">Skip</button>
          </div>
        </div>
      ) : (
        <div className="ss-card-premium p-6 sm:p-8 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-cocoa/50">What happened?</span>
            <textarea data-testid="onboarding-what" value={what} onChange={(e) => setWhat(e.target.value)} rows={6}
              placeholder="The way you'd tell a friend over coffee. Your observations are enough."
              className="mt-1 w-full bg-ivory border border-champagne rounded-md p-4 outline-none focus:border-mauve leading-relaxed" />
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setStep(1)} className="ss-pill ss-pill-ghost h-12">Back</button>
            <button onClick={createProfileAndAsk} disabled={busy || what.trim().length < 4} data-testid="onboarding-submit"
              className="ss-pill ss-pill-primary h-12 flex-1 disabled:opacity-50">
              {busy ? "Setting up…" : <><Sparkles size={14} /> Read the pattern</>}
            </button>
          </div>
          <button onClick={skipAll} className="text-xs text-cocoa/50 hover:text-cocoa">Skip onboarding</button>
        </div>
      )}
    </div>
  );
}
